// Virtualised message timeline.
//
// Implementation notes:
//
// - Rendering is driven by `@tanstack/react-virtual`. `overscan={20}`
//   keeps ~20 items above and below the viewport, meeting the brief's
//   ~50–100 DOM-node window irrespective of history size.
// - Dynamic row heights via `measureElement` — message bodies wrap,
//   which makes fixed estimates unreliable. A 72px initial estimate
//   (one grouped row + body) keeps initial scroll position honest.
// - Scroll-position preservation on backward pagination uses the
//   virtualizer's `scrollToIndex` against an anchor index captured
//   before prepending. `@tanstack/react-virtual` updates the container
//   height asynchronously as prepended rows are measured, so the plain
//   "delta scrollTop" approach would drift once real heights arrive.
//   Anchoring on an event id dodges the drift: the virtualizer keeps
//   that row pinned regardless of how much the size estimates change
//   during measurement.
// - Auto-scroll to bottom fires only when the user is within 50px of
//   the bottom — a hard rule from the brief. New messages arriving
//   while scrolled up increment the FAB's counter instead; state
//   events (joins/topic changes) do not count.
// - Read receipts: fire a debounced receipt whenever the user is at
//   the bottom — from scroll, programmatic scroll-to-bottom, mount,
//   and when a new message arrives while sticking to the bottom.
//   `events` is read through a ref so `scheduleReadReceipt` stays
//   stable and callers don't leak stale closures.
// - Accessibility: the scroll container itself is NOT a live region —
//   virtualisation would fire announcements every time a row mounts /
//   unmounts. A separate visually-hidden live region announces
//   new-message counts.

import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type AnimationEvent as ReactAnimationEvent,
  type ComponentProps,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { elementScroll, type VirtualItem, useVirtualizer } from "@tanstack/react-virtual";
import { markBoot, reportBoot } from "~/lib/boot-timing";
import { ArrowUpRight, ChevronLeft, Lock, Reply } from "lucide-react";

import { FileAttachment } from "~/components/FileAttachment";
import { DropZone, FileUploadButton, UploadProgressRow } from "~/components/FileUpload";
import { useImageLightbox } from "~/components/ImageLightbox";
import { MediaPreview } from "~/components/MediaPreview";
import { AudioMedia, VideoMedia } from "~/components/MediaPlayer";
import { MessageActions, buildPinAction, type MessageAction } from "~/components/MessageActions";
import {
  MessageBubble,
  avatarSizeFor,
  avatarThumbnail,
  type Density,
  type TimelineMessage,
} from "~/components/MessageBubble";
import { MeetingBanner } from "~/components/MeetingBanner";
import {
  MessageComposer,
  type AutocompleteProvider,
  type AutocompleteProviderResolver,
  type ComposerSubmitMeta,
} from "~/components/MessageComposer";
import { AgentMentionPrompt } from "~/components/AgentMentionPrompt";
import { PinnedBanner } from "~/components/PinnedBanner";
import { RequestKeysButton } from "~/components/RequestKeysButton";
import { TypingIndicator } from "~/components/TypingIndicator";
import { useDraft } from "~/hooks/useDraft";
import { useDnd } from "~/hooks/useDnd";
import { useSettled } from "~/hooks/useSettled";
import { useAvatarReveal } from "~/hooks/useAvatarReveal";
import {
  useEditRecall,
  type EditRecallOlderResult,
  type EditRecallOrigin,
} from "~/hooks/useEditRecall";
import { useFileUpload } from "~/hooks/useFileUpload";
import { useTauriFileDrop } from "~/hooks/useTauriFileDrop";
import {
  mediaSourceKey,
  objectUrlFromBytes,
  useMediaObjectUrl,
  useStableMediaSource,
} from "~/hooks/useMediaObjectUrl";
import { usePinned } from "~/hooks/usePinned";
import { useMeetingActive } from "~/hooks/useMeetingPresence";
import { useOwnAgent, buildOwnAgentSnapshot } from "~/hooks/useOwnAgent";
import { useOwnAgentMentionGate } from "~/hooks/useOwnAgentMentionGate";
import { useTimeline } from "~/hooks/useTimeline";
import { useUtdReasonBreadcrumb } from "~/hooks/useUtdReasonBreadcrumb";
import { useAgentCommands } from "~/hooks/useAgentCommands";
import type { PendingJump } from "~/context/MatrixContext";
import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import { useToast } from "~/context/ToastContext";
import {
  type OwnAgentSnapshot,
  resolveEmojiSuggestions,
  resolveRoomSuggestions,
  resolveUserSuggestions,
} from "~/lib/autocomplete-providers";
import {
  canMentionRoom,
  getMembers,
  getReceiptsForEvent,
  inviteUser,
  isThreadReply,
  requestKeysForEvent,
  type MatrixMediaSource,
  sendReadReceipt as matrixSendReadReceipt,
  resendLocalEcho,
  sendEmote,
  sendRawMessage,
  sendText,
  setPresence,
  setTyping as matrixSetTyping,
  subscribeReceipts,
  subscribeReadMarker,
  subscribeTyping,
  type Client,
  type MessageEncryptionStatus,
  type Receipt,
  type RemovedBySelf,
  type TimelineEvent,
  type TimelineEventContent,
  type UserDirectoryEntry,
} from "~/lib/matrix";
import { latestReceiptPerUser, lastReceiptableEvent } from "~/components/timeline-receipts";
import { agentCommandContent, type SlashCommand, type SlashCtx } from "~/lib/slash-commands";
import {
  computeGroupPositions,
  formatDateSeparator,
  formatMessageTime,
  formatTimeOfDay,
  GROUP_WINDOW_MS,
  isSameDay,
  isStateEvent,
  isUndecryptableTimelineEvent,
  messageContentBody,
  messageMentions,
  roomTombstoneReplacement,
  stateEventLabel,
} from "~/lib/message-utils";
import type { QuoteComposerHandle } from "~/lib/quote";
import { schedule as scheduleMessage } from "~/lib/scheduled-messages";
import { matrixLocalpart } from "~/lib/display-name";
import { buildReceiptAvatarMap } from "~/lib/room-utils";
import { loadRowSizes, saveRowSizes } from "~/lib/row-size-cache";
import { ICON_STROKE } from "~/lib/icon";

const PAGE_SIZE = 50;
const AUTO_SCROLL_THRESHOLD_PX = 50;
const PAGINATION_THRESHOLD_PX = 200;
const READING_ANCHOR_USER_SCROLL_THRESHOLD_PX = 160;
const READ_RECEIPT_DEBOUNCE_MS = 400;
const ESTIMATED_ROW_HEIGHT_PX = 72;
const OVERSCAN = 20;
const AUTOCOMPLETE_EMPTY_STATE_MESSAGES = { "#": "No matches" };
// Matrix typing timeout — the server clears peers' indicators after
// this window if no refresh arrives. Pinned in the brief at 30s.
const TYPING_TIMEOUT_MS = 30_000;
const USER_SCROLL_SETTLE_MS = 150;

const STANDALONE_PIN_STYLE_ID = "chat-standalone-pin-action-styles";

function isVisibleTimelineEvent(event: TimelineEvent): boolean {
  if (isThreadReply(event) || event.type === "m.reaction" || event.type === "m.room.redaction") {
    return false;
  }
  // An upgraded-room tombstone with a successor is surfaced by a banner,
  // not a timeline row.
  if (roomTombstoneReplacement(event)) return false;
  if (isUndecryptableTimelineEvent(event)) return true;
  if (isStateEvent(event)) return Boolean(stateEventLabel(event));
  switch (event.content.type) {
    case "m.text":
    case "m.notice":
    case "m.emote":
    case "m.image":
    case "m.video":
    case "m.audio":
    case "m.file":
    case "m.redacted":
    case "m.unsupported":
      return true;
    default:
      return false;
  }
}

function revokeBlobUrl(url: string | undefined): void {
  if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
}

// Media-specific UTD (encryptionStatus "undecryptable") and sourceless
// media must never reach `MessageBubble` — see `Timeline.fabricated-text.test.tsx`.
function isChromedMediaEvent(event: TimelineEvent, c: TimelineEventContent): boolean {
  if (event.encryptionStatus === "undecryptable") return false;
  if (c.type !== "m.image" && c.type !== "m.video" && c.type !== "m.audio" && c.type !== "m.file") {
    return false;
  }
  return (c.source ?? mediaSourceFromMxc(c.url)) !== null;
}

function injectStandalonePinStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(STANDALONE_PIN_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STANDALONE_PIN_STYLE_ID;
  style.textContent = `
    .chat-standalone-pin-action {
      opacity: 0;
      pointer-events: none;
      transition: opacity var(--chat-motion-fast, 120ms) var(--chat-motion-ease, ease);
    }
    .chat-standalone-pin-row:hover .chat-standalone-pin-action,
    .chat-standalone-pin-row:focus-within .chat-standalone-pin-action {
      opacity: 1;
      pointer-events: auto;
    }
  `;
  document.head.appendChild(style);
}

injectStandalonePinStyles();

export interface TimelineProps {
  client: Client;
  roomId: string;
  density?: Density;
  /** Placeholder for the composer, e.g. "Message #design". */
  composerPlaceholder?: string;
  /** When set, the timeline scrolls to `eventId` and flashes it once
   *  the event lands in the buffer. Paginates backward as needed. The
   *  `token` distinguishes repeated jumps to the same event. */
  pendingJump?: PendingJump | null;
  /** Called after the jump has been applied so the parent can clear
   *  its `pendingJump` state and avoid re-firing on the next render. */
  onJumpApplied?: () => void;
  /** Called when the user opens a thread on a message. The full event
   *  is forwarded so the opener (typically the embed / standalone
   *  shell) can store the root event in a single-slot thread state
   *  without an additional SDK lookup. The Timeline already has the
   *  event in scope via the `MessageBubble` row, so resolving `id` →
   *  `event` happens here, not at the call site. */
  onOpenThread?: (root: TimelineEvent) => void;
  /** Opens global search with a prefilled query. */
  onOpenSearch?: (query: string) => void;
  /** When set, the timeline renders a compact header with a back
   *  affordance that returns to the room list. Used by the narrow
   *  `mode="full"` embed (<400px) where the sidebar is not mounted
   *  beside the timeline; the standalone shell and wider embeds leave
   *  it unset (the sidebar is the navigation there). */
  onNavigateBack?: () => void;
  /** Room name shown beside the back affordance in the narrow header. */
  navigateBackTitle?: string;
  archived?: boolean;
  archivedReadOnly?: boolean;
  removedBySelf?: RemovedBySelf | null;
  composerRef?: RefObject<QuoteComposerHandle | null>;
  quoteComposerRef?: RefObject<QuoteComposerHandle | null>;
  /**
   * Touch-first layout for the mobile shell. Drops the per-message
   * hover-action gutter and avatar column (long-press opens the action
   * sheet instead via `onLongPressMessage`) and switches the composer
   * to its underline mobile variant. Virtualisation, grouping, day
   * dividers, reactions, and the typing indicator are unchanged.
   */
  mobile?: boolean;
  /**
   * Called when a message row is long-pressed on mobile. The shell
   * opens the `MessageSheet` for the event. Only wired when `mobile`.
   */
  onLongPressMessage?: (event: TimelineEvent, anchor: HTMLElement | null) => void;
  /**
   * Receives an imperative controller so the mobile action sheet can
   * drive the timeline's own inline editor (the editor state lives
   * here, keyed to the virtual row). Set once the timeline mounts.
   */
  editControllerRef?: RefObject<TimelineEditController | null>;
}

export interface TimelineEditController {
  openEdit: (eventId: string) => void;
}

export function shouldPaginateEditRecallBoundary(
  result: EditRecallOlderResult,
  atStart: boolean,
  isLoading: boolean,
): boolean {
  return (result === "at-oldest" || result === "empty") && !atStart && !isLoading;
}

type TimelineRow =
  | {
      kind: "event";
      key: string;
      event: TimelineEvent;
      group: ReturnType<typeof computeGroupPositions>[number];
    }
  | {
      kind: "state-run";
      key: string;
      events: TimelineEvent[];
    }
  | {
      /** Consecutive messages this device cannot decrypt, across days:
       *  one quiet row instead of a "Request keys" button per message. */
      kind: "locked-run";
      key: string;
      events: TimelineEvent[];
    }
  | { kind: "date"; key: string; label: string }
  | { kind: "unread-boundary"; key: string; eventId: string };

// Keep the first viewport-intersecting event at its captured pixel offset
// across loading, prepend, and virtualizer remeasurement.

export interface ReadingAnchor {
  roomId: string;
  eventId: string;
  viewportOffset: number;
  /** Completion generation when pagination starts. */
  requestId: number;
}

// A page with events still requires correction, even when it reaches at-start.
function isTerminalPagination(
  outcome: { loaded: number; atStart: boolean; failed: boolean } | null,
): boolean {
  if (!outcome) return false;
  if (outcome.failed) return true;
  return outcome.loaded === 0;
}

export interface ReadingAnchorContext {
  roomId: string;
  /** Current `eventRect.top - scrollerRect.top`; null when the anchor
   *  row isn't measurable right now (culled, mid-remount, off-screen). */
  currentOffset: number | null;
  /** Deliberate user scroll (wheel / touch) since the anchor was captured. */
  userMoved: boolean;
  /** True once the triggering request has settled. */
  settled: boolean;
  /** Outcome of the most recent settled pagination. */
  outcome: { loaded: number; atStart: boolean; failed: boolean } | null;
}

// Correction is measuredOffset - capturedOffset, never scrollHeight delta.
export function resolveReadingAnchor(
  anchor: ReadingAnchor | null,
  ctx: ReadingAnchorContext,
): { correction: number; release: boolean } {
  if (!anchor || anchor.roomId !== ctx.roomId) return { correction: 0, release: true };
  if (ctx.userMoved) return { correction: 0, release: true };
  // A previous request's outcome is stale until this generation settles.
  const terminal = ctx.settled && isTerminalPagination(ctx.outcome);
  if (terminal) return { correction: 0, release: true };
  // A prepend may cull the watched row; mount its index before measuring.
  if (ctx.currentOffset === null) {
    return { correction: 0, release: false };
  }
  const correction = ctx.currentOffset - anchor.viewportOffset;
  // Hold the reader through late row remeasurements rather than releasing
  // on the first sub-pixel settled pass: a page can converge to within a
  // pixel of the captured offset before a partially visible image or font
  // loads, then drift beyond the ticket's bound. Release only when the
  // reader moves, a terminal page lands, the room switches, or the watched
  // row goes missing without an index to re-mount.
  return { correction, release: false };
}

export function supersedesReadingAnchorOnInput(
  anchor: ReadingAnchor | null,
  captureScrollTop: number | null,
  currentScrollTop: number,
  correctionApplied: boolean,
): boolean {
  if (!anchor) return false;
  if (correctionApplied) return true;
  return (
    captureScrollTop !== null &&
    Math.abs(currentScrollTop - captureScrollTop) > READING_ANCHOR_USER_SCROLL_THRESHOLD_PX
  );
}

/* ── Unread boundary ──────────────────────────────────────────── */
//
// A single `New` separator sits immediately before the first visible
// event after the read marker captured at room entry. It is keyed
// `unread:<eventId>` (never a timeline event) so it is excluded from
// message totals and grouping. The marker is established once: a late
// initial marker may set it, but a subsequent own-receipt advance must
// not chase it down the screen.

export function retainEntryReadMarker(
  captured: string | null,
  incoming: string | null,
): string | null {
  return captured ?? incoming;
}

/**
 * Resolve the single `New` boundary from a read marker. The marker is
 * located in the RAW buffer (a thread-reply marker survives), then the
 * first visible event strictly after it gives the separator's id. When
 * the marker sits inside the window but is the last visible event, the
 * boundary is still `established` with a null id: the slot is frozen so a
 * later live message (including your own local echo) cannot grow a `New`
 * mid-visit. Absent or outside-window markers stay unestablished —
 * pagination may resolve them.
 */
export function resolveUnreadBoundary(
  events: readonly TimelineEvent[],
  visiblePredicate: (event: TimelineEvent) => boolean,
  markerEventId: string | null,
): { established: boolean; eventId: string | null } {
  if (!markerEventId) return { established: false, eventId: null };
  const markerIndex = events.findIndex((event) => event.id === markerEventId);
  if (markerIndex === -1) return { established: false, eventId: null };
  for (let i = markerIndex + 1; i < events.length; i += 1) {
    const event = events[i];
    if (event && visiblePredicate(event)) return { established: true, eventId: event.id };
  }
  return { established: true, eventId: null };
}

/**
 * Find the rendered row node for an event id. Iterates `[data-event-id]`
 * nodes rather than building a selector so ids with special characters
 * (`$…:server`) don't break a CSS attribute query. Returns null when the
 * node isn't currently mounted (culled by the virtualizer, off-screen).
 */
export function findEventRowNode(parent: HTMLElement | null, eventId: string): HTMLElement | null {
  if (!parent || !eventId) return null;
  const nodes = parent.querySelectorAll<HTMLElement>("[data-event-id]");
  for (const node of Array.from(nodes)) {
    if (node.getAttribute("data-event-id") === eventId) return node;
  }
  return null;
}

/**
 * Current offset of an event's row above the scroller's content-top
 * (`eventRect.top - scrollerRect.top`), or null when unmeasurable. This
 * is the value the reading-anchor compares against its captured offset.
 */
export function measureEventOffset(parent: HTMLElement | null, eventId: string): number | null {
  const node = findEventRowNode(parent, eventId);
  if (!node || !parent) return null;
  return node.getBoundingClientRect().top - parent.getBoundingClientRect().top;
}

/** Every mounted event row inside the viewport, top to bottom. */
function viewportRowPoints(parent: HTMLElement): Array<{ eventId: string; offset: number }> {
  const scrollerTop = parent.getBoundingClientRect().top;
  const foldBottom = scrollerTop + parent.clientHeight;
  const points: Array<{ eventId: string; offset: number }> = [];
  for (const node of Array.from(parent.querySelectorAll<HTMLElement>("[data-event-id]"))) {
    const rect = node.getBoundingClientRect();
    const eventId = node.getAttribute("data-event-id");
    if (!eventId || rect.bottom <= scrollerTop || rect.top >= foldBottom) continue;
    points.push({ eventId, offset: rect.top - scrollerTop });
  }
  return points.sort((a, b) => a.offset - b.offset);
}

function isReconciling<T extends object>(instance: T): boolean {
  return (instance as unknown as { scrollState: unknown }).scrollState !== null;
}

/**
 * Topmost currently-mounted event row that intersects the scroller
 * viewport, with its offset above the content-top (`rect.top -
 * scrollerRect.top`). Rows fully above or below the fold are skipped,
 * so a culled or off-screen first-loaded event is never pinned to a
 * defaulted `0` offset. Returns null when no row is in view — the
 * caller still paginates but holds no anchor instead of pinning an
 * unmeasurable row. `avoidEventId` is passed over while another row is in
 * view: the oldest loaded row loses its sender header once an earlier
 * message from the same sender is prepended, which moves the rows below it.
 */
export function firstViewportIntersectingRow(
  parent: HTMLElement | null,
  avoidEventId?: string,
): { eventId: string; offset: number } | null {
  if (!parent) return null;
  const scrollerTop = parent.getBoundingClientRect().top;
  const foldBottom = scrollerTop + parent.clientHeight;
  let chosen: { eventId: string; offset: number } | null = null;
  let avoided: { eventId: string; offset: number } | null = null;
  const nodes = parent.querySelectorAll<HTMLElement>("[data-event-id]");
  for (const node of Array.from(nodes)) {
    const rect = node.getBoundingClientRect();
    // Skip rows fully above or below the fold; keep the topmost in-view.
    if (rect.bottom <= scrollerTop) continue;
    if (rect.top >= foldBottom) continue;
    const id = node.getAttribute("data-event-id") ?? "";
    if (!id) continue;
    const offset = rect.top - scrollerTop;
    if (id === avoidEventId) avoided = { eventId: id, offset };
    else if (chosen === null || offset < chosen.offset) chosen = { eventId: id, offset };
  }
  return chosen ?? avoided;
}

// Touch rows carry no clock; time surfaces as a quiet mark when a conversation resumes after a pause.
const TIME_MARK_GAP_MS = 60 * 60 * 1000;

function buildTimelineRows(
  visibleEvents: TimelineEvent[],
  timeMarks = false,
  rowKey: (eventId: string) => string = (eventId) => eventId,
): TimelineRow[] {
  const renderableEvents = visibleEvents.filter(isVisibleTimelineEvent);
  const groups = computeGroupPositions(renderableEvents);
  const out: TimelineRow[] = [];
  let lastRenderedTs: number | null = null;
  function pushDateIfNeeded(ts: number): void {
    if (lastRenderedTs !== null && isSameDay(lastRenderedTs, ts)) {
      if (!timeMarks || ts - lastRenderedTs < TIME_MARK_GAP_MS) return;
      out.push({ kind: "date", key: `time-${ts}`, label: formatTimeOfDay(ts) });
      return;
    }
    out.push({
      kind: "date",
      key: `date-${new Date(ts).toDateString()}`,
      label: timeMarks
        ? `${formatDateSeparator(ts)} \u00b7 ${formatTimeOfDay(ts)}`
        : formatDateSeparator(ts),
    });
  }
  for (let i = 0; i < renderableEvents.length; i += 1) {
    const current = renderableEvents[i];
    if (!current) continue;
    if (isUndecryptableTimelineEvent(current)) {
      const locked = [current];
      let j = i + 1;
      while (j < renderableEvents.length) {
        const next = renderableEvents[j];
        if (!next || !isUndecryptableTimelineEvent(next)) break;
        if (isHistoryNotShared(next) !== isHistoryNotShared(current)) break;
        locked.push(next);
        j += 1;
      }
      // No separator before the run (it states its own date range); the
      // next readable message gets its day again.
      out.push({ kind: "locked-run", key: `locked-run-${current.id}`, events: locked });
      lastRenderedTs = null;
      i = j - 1;
      continue;
    }
    if (!isStateEvent(current)) {
      pushDateIfNeeded(current.ts);
      const group = groups[i] ?? "single";
      out.push({ kind: "event", key: rowKey(current.id), event: current, group });
      lastRenderedTs = current.ts;
      continue;
    }
    const run = [current];
    let j = i + 1;
    while (j < renderableEvents.length) {
      const next = renderableEvents[j];
      const previous = renderableEvents[j - 1];
      if (!next || !previous) break;
      if (!isStateEvent(next)) break;
      if (!isSameDay(current.ts, next.ts)) break;
      if (next.ts - previous.ts > GROUP_WINDOW_MS) break;
      run.push(next);
      j += 1;
    }
    if (run.length === 1) {
      pushDateIfNeeded(current.ts);
      const group = groups[i] ?? "single";
      out.push({ kind: "event", key: rowKey(current.id), event: current, group });
      lastRenderedTs = current.ts;
    } else {
      pushDateIfNeeded(current.ts);
      const last = run[run.length - 1] ?? current;
      out.push({ kind: "state-run", key: `state-run-${last.id}`, events: run });
      lastRenderedTs = last.ts;
      i = j - 1;
    }
  }
  return out;
}

function buildRowIndexByEventId(rows: TimelineRow[]): Map<string, number> {
  const map = new Map<string, number>();
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    if (!row) continue;
    if (row.kind === "event") map.set(row.event.id, i);
    if (row.kind === "state-run" || row.kind === "locked-run") {
      for (const event of row.events) map.set(event.id, i);
    }
  }
  return map;
}

function sameReceiptPairs(a: Receipt[], b: Receipt[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i];
    const right = b[i];
    if (!left || !right || left.userId !== right.userId || left.ts !== right.ts) return false;
  }
  return true;
}

function sameElements(a: readonly unknown[], b: readonly unknown[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function equivalentTimelineRow(a: TimelineRow, b: TimelineRow): boolean {
  if (a.kind !== b.kind || a.key !== b.key) return false;
  switch (a.kind) {
    case "event":
      return b.kind === "event" && a.event === b.event && a.group === b.group;
    case "date":
      return b.kind === "date" && a.label === b.label;
    case "unread-boundary":
      return b.kind === "unread-boundary" && a.eventId === b.eventId;
    case "state-run":
    case "locked-run":
      if (b.kind === "state-run" || b.kind === "locked-run") {
        return sameElements(a.events, b.events);
      }
      return false;
    default:
      return false;
  }
}

function reusePreviousRows(
  rows: TimelineRow[],
  prevByKey: Map<string, TimelineRow>,
): TimelineRow[] {
  if (prevByKey.size === 0) return rows;
  const out: TimelineRow[] = [];
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    if (!row) continue;
    const prior = prevByKey.get(row.key);
    if (prior && equivalentTimelineRow(prior, row)) {
      out.push(prior);
      continue;
    }
    out.push(row);
  }
  return out;
}

function indexRowsByKey(rows: TimelineRow[]): Map<string, TimelineRow> {
  const map = new Map<string, TimelineRow>();
  for (const row of rows) map.set(row.key, row);
  return map;
}

function collectSenderAvatarMxcs(events: TimelineEvent[]): string[] {
  const seen = new Set<string>();
  for (const event of events) {
    const avatarUrl = event.sender.avatarUrl;
    if (avatarUrl?.startsWith("mxc://")) seen.add(avatarUrl);
  }
  return [...seen];
}

export function Timeline({
  client,
  roomId,
  density = "comfortable",
  composerPlaceholder,
  pendingJump,
  onJumpApplied,
  onOpenThread,
  onOpenSearch,
  onNavigateBack,
  navigateBackTitle,
  archived = false,
  archivedReadOnly = false,
  removedBySelf = null,
  composerRef,
  quoteComposerRef,
  mobile = false,
  onLongPressMessage,
  editControllerRef,
}: TimelineProps) {
  const { events, isLoading, atStart, paginationGeneration, lastPagination, paginateBackward } =
    useTimeline(client, roomId, PAGE_SIZE);
  useUtdReasonBreadcrumb(roomId, events);
  const upload = useFileUpload(undefined, () => (mobile ? parentRef.current : null));
  const { push: pushToast } = useToast();
  const { setManualUntil: setDndUntil, setOff: setDndOff } = useDnd();
  const {
    searchUserDirectory,
    searchRooms,
    findDirectRoom,
    createDirectRoom,
    setActiveRoomId,
    userId,
    jumpToEvent,
  } = useMatrix();
  const rooms = useMatrixRooms();
  const activeMeeting = useMeetingActive(client, roomId);
  const pinned = usePinned(client, roomId, userId, events);
  const { pins, canManage: canManagePins, isPinned, pin, unpin, clear: clearPins } = pinned;
  const { openImage: handleOpenImage, lightbox } = useImageLightbox();

  const parentRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const hasDoneInitialScrollRef = useRef(false);
  // In-flight guard for `handleRetry` — without it a double-tap fires a
  // second `resendLocalEcho` before the first settles, and the loser's
  // rejection (the echo it targets already moved past "failed") is
  // never caught, surfacing as an unhandled rejection.
  const retryInFlightRef = useRef<Set<string>>(new Set());
  // Id snapshot of events from the previous render. Used to
  // accurately derive "new messages" even when events are prepended
  // via pagination (a naïve `nextCount - prevCount` would count every
  // prepended event as new).
  const prevEventIdsRef = useRef<Set<string>>(new Set());
  const readingAnchorRef = useRef<ReadingAnchor | null>(null);
  // Where the reader is outside pagination, so a replaced or reshaped buffer
  // (cache refresh, redaction, moved thread reply) can be restored by measurement.
  const readingPointRef = useRef<Array<{ eventId: string; offset: number }>>([]);
  // Deliberate user scroll (wheel / touch) supersedes a stale capture.
  const userMovedRef = useRef(false);
  const anchorCaptureScrollTopRef = useRef<number | null>(null);
  const anchorCorrectionAppliedRef = useRef(false);
  const anchorMountAttemptRef = useRef<string | null>(null);
  const userInputPendingRef = useRef(false);
  // Match the resulting offset, not a frame-wide flag: momentum may
  // deliver a different scroll before the programmatic event arrives.
  const observedScrollTopRef = useRef<number | null>(null);
  const programmaticScrollRef = useRef<number | null>(null);
  const touchActiveRef = useRef(false);
  const lastUserScrollAtRef = useRef(0);
  const userScrolling = useCallback(
    () =>
      touchActiveRef.current ||
      performance.now() - lastUserScrollAtRef.current < USER_SCROLL_SETTLE_MS,
    [],
  );
  const readReceiptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastReceiptEventIdRef = useRef<string | null>(null);
  const eventsRef = useRef(events);
  eventsRef.current = events;
  // Raw SDK buffer keeps thread-only backward pages traversable.
  const visibleEventsRef = useRef<TimelineEvent[]>([]);
  const prevRowsByKeyRef = useRef<Map<string, TimelineRow>>(new Map());

  const [newSinceFabShown, setNewSinceFabShown] = useState(0);
  const [fabVisible, setFabVisible] = useState(false);
  const [discardedEchoIds, setDiscardedEchoIds] = useState<Set<string>>(() => new Set());
  const [expandedStateEventIds, setExpandedStateEventIds] = useState<Set<string>>(() => new Set());
  // The event currently flashing. The row renders `data-flash="1"`
  // which the CSS animation consumes. Cleared after
  // `--chat-motion-flash` elapses. `token` bumps on every new jump so
  // repeated jumps to the same event restart the animation.
  const [flashing, setFlashing] = useState<{ eventId: string; token: number } | null>(null);
  // `appliedTokenRef` remembers the token we last notified the parent
  // about so we don't double-fire `onJumpApplied` during the one-frame
  // window between our state change and the parent's prop update.
  const appliedTokenRef = useRef<number | null>(null);
  const [typingUserIds, setTypingUserIds] = useState<string[]>([]);
  const [receiptEpoch, setReceiptEpoch] = useState(0);
  // Two-slot live announcement: alternates between `a` and `b` so a
  // repeated summary ("New message" twice in a row) forces a real DOM
  // update. One slot clears while the other carries the new text —
  // the screen reader announces whichever changed.
  const [liveAnnouncement, setLiveAnnouncement] = useState<{
    slot: "a" | "b";
    text: string;
  }>({ slot: "a", text: "" });
  // Freeze the entry marker; own receipts must not chase the separator.
  const [unreadBoundary, setUnreadBoundary] = useState<{
    established: boolean;
    eventId: string | null;
  }>({ established: false, eventId: null });

  // Thread replies belong to ThreadPanel, not the room feed.
  const visibleEvents = useMemo<TimelineEvent[]>(
    () =>
      events.filter((event) => isVisibleTimelineEvent(event) && !discardedEchoIds.has(event.id)),
    [events, discardedEchoIds],
  );
  visibleEventsRef.current = visibleEvents;

  // A room upgrade tombstones the old room and names its successor in
  // `m.room.tombstone.replacementRoom`. When the loaded window carries
  // one we surface the move as a banner (and disable the composer) so
  // members aren't stranded on a raw room id, rather than folding the
  // tombstone into the collapsed "room updates" line. The newest
  // tombstone wins.
  const replacementRoom = useMemo<string | null>(() => {
    let best: string | null = null;
    let bestTs = Number.NEGATIVE_INFINITY;
    for (const event of events) {
      const rr = roomTombstoneReplacement(event);
      if (rr && event.ts >= bestTs) {
        best = rr;
        bestTs = event.ts;
      }
    }
    return best;
  }, [events]);

  // A sent message keeps its local echo's row key after the server id
  // replaces it, so React and the virtualizer keep the same row (and its
  // measured height) instead of remounting it.
  const echoRowKeysRef = useRef(new Map<string, string>());
  // Pending pagination never changes measured row geometry.
  const rows = useMemo<TimelineRow[]>(() => {
    const echoRowKeys = echoRowKeysRef.current;
    for (const event of visibleEvents) {
      if (event.localEchoId) echoRowKeys.set(event.id, event.localEchoId);
    }
    const built = buildTimelineRows(
      visibleEvents,
      mobile,
      (eventId) => echoRowKeys.get(eventId) ?? eventId,
    );
    if (!unreadBoundary.established) return reusePreviousRows(built, prevRowsByKeyRef.current);
    const boundaryEventId = unreadBoundary.eventId;
    if (!boundaryEventId) return reusePreviousRows(built, prevRowsByKeyRef.current);
    const targetIndex = built.findIndex((row) => rowContainsEvent(row, boundaryEventId));
    if (targetIndex === -1) return reusePreviousRows(built, prevRowsByKeyRef.current);
    const injected: TimelineRow[] = [];
    built.forEach((row, i) => {
      if (i === targetIndex) {
        injected.push({
          kind: "unread-boundary",
          key: `unread:${boundaryEventId}`,
          eventId: boundaryEventId,
        });
      }
      injected.push(row);
    });
    return reusePreviousRows(injected, prevRowsByKeyRef.current);
  }, [visibleEvents, mobile, unreadBoundary]);
  useEffect(() => {
    prevRowsByKeyRef.current = indexRowsByKey(rows);
  }, [rows]);

  // Fast lookup from event id → row index so `scrollToIndex` can
  // target the pagination anchor without scanning `rows` linearly each
  // time layout settles.
  const rowIndexByEventId = useMemo(() => buildRowIndexByEventId(rows), [rows]);
  const {
    current: editingEventId,
    origin: editOrigin,
    open: openEdit,
    recallOlder,
    recallNewer,
    exit: exitEdit,
  } = useEditRecall(visibleEvents, userId);

  useEffect(() => {
    if (!editControllerRef) return;
    editControllerRef.current = { openEdit: (eventId) => openEdit(eventId, "manual") };
    return () => {
      editControllerRef.current = null;
    };
  }, [editControllerRef, openEdit]);

  useEffect(() => {
    if (events.length === 0 || visibleEvents.length > 0 || isLoading || atStart) return;
    void paginateBackward(PAGE_SIZE);
  }, [atStart, events.length, isLoading, paginateBackward, visibleEvents.length]);

  // Receipts snapshot for every event in the buffer. Computing once
  // per `events` / `receiptEpoch` change (rather than once per row
  // per render) caps allocation at the update cadence — `MessageBubble`
  // receives a stable array reference for events whose receipt list
  // hasn't materially changed, preserving downstream memoization.
  //
  // `receiptEpoch` bumps on `subscribeReceipts` notifications so
  // receipt-only /sync deliveries (no new timeline event) still
  // refresh the avatar stack.
  const receiptsPrevRef = useRef<Map<string, Receipt[]>>(new Map());
  const receiptsByEventId = useMemo(() => {
    // receiptEpoch is an explicit invalidation key; no other role.
    void receiptEpoch;
    const previous = receiptsPrevRef.current;
    const received = (eventId: string) => getReceiptsForEvent(client, roomId, eventId);
    const latest = latestReceiptPerUser(visibleEvents, received);
    const map = new Map<string, Receipt[]>();
    for (const [eventId, list] of latest) {
      const reused = previous.get(eventId);
      const chosen = reused && sameReceiptPairs(reused, list) ? reused : list;
      map.set(eventId, chosen);
    }
    return map;
  }, [client, roomId, visibleEvents, receiptEpoch]);
  useEffect(() => {
    receiptsPrevRef.current = receiptsByEventId;
  }, [receiptsByEventId]);

  const [initialSizes] = useState(() => loadRowSizes(roomId, Math.round(window.innerWidth)));

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT_PX,
    initialMeasurementsCache: initialSizes,
    overscan: OVERSCAN,
    getItemKey: (index) => rows[index]?.key ?? index,
    scrollToFn: (offset, options, instance) => {
      const previousOffset = instance.scrollElement?.scrollTop;
      elementScroll(offset, options, instance);
      const nextOffset = instance.scrollElement?.scrollTop;
      if (nextOffset !== previousOffset) {
        observedScrollTopRef.current = nextOffset ?? null;
        programmaticScrollRef.current = nextOffset ?? null;
      }
    },
  });
  // A row above the fold that a user scroll brings into the overscan is
  // measured late; offset its resize so the text under the reader stays put.
  // The element's scrollTop already includes corrections made this frame.
  virtualizer.shouldAdjustScrollPositionOnItemSizeChange = (item, _delta, instance) =>
    isReconciling(instance) ||
    (userScrolling() && item.start < (instance.scrollElement?.scrollTop ?? 0));
  const virtualTotalSize = virtualizer.getTotalSize();
  const oldestEventId = visibleEvents[0]?.id;
  // Opening a room measures rows and re-pins the bottom for a few frames;
  // keep the list invisible until its height holds still so it doesn't wiggle.
  const hasRows = visibleEvents.length > 0;
  const layoutSettled = useSettled(roomId, hasRows, virtualTotalSize);

  const lastMeasuredRef = useRef<{
    roomId: string;
    width: number;
    items: VirtualItem[];
  } | null>(null);
  useLayoutEffect(() => {
    lastMeasuredRef.current = {
      roomId,
      width: Math.round(window.innerWidth),
      items: virtualizer.measurementsCache,
    };
  });
  useLayoutEffect(() => {
    return () => {
      const snapshot = lastMeasuredRef.current;
      if (!snapshot || snapshot.roomId !== roomId) return;
      saveRowSizes(snapshot.roomId, snapshot.width, snapshot.items.slice());
    };
  }, [roomId]);

  const avatarThumb = useMemo(() => avatarThumbnail(avatarSizeFor(density)), [density]);
  const avatarMxcs = useMemo(() => collectSenderAvatarMxcs(visibleEvents), [visibleEvents]);
  const avatarsSettled = useAvatarReveal(client, roomId, hasRows, avatarThumb, avatarMxcs);
  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    if (visibleEvents.length > 0 && layoutSettled && avatarsSettled) {
      markBoot("timeline-first");
      reportBoot();
    }
  }, [visibleEvents, layoutSettled, avatarsSettled]);

  // Debounced read receipt. Stable across renders — reads the visible
  // (non-thread) buffer through `visibleEventsRef` so the receipt
  // never targets a thread reply the user can't see in the main feed.
  // If the receipt landed on a hidden thread reply, the visual unread
  // dot would clear without the user ever scrolling past the message
  // that was actually rendered.
  const scheduleReadReceipt = useCallback(() => {
    if (readReceiptTimerRef.current) clearTimeout(readReceiptTimerRef.current);
    readReceiptTimerRef.current = setTimeout(() => {
      const last = lastReceiptableEvent(visibleEventsRef.current);
      if (!last) return;
      if (lastReceiptEventIdRef.current === last.id) return;
      lastReceiptEventIdRef.current = last.id;
      void matrixSendReadReceipt(client, roomId, last.id);
    }, READ_RECEIPT_DEBOUNCE_MS);
  }, [client, roomId]);

  // Scroll listener: track stick-to-bottom, trigger pagination near
  // the top, send read receipts when the user scrolls near the bottom.
  const onScroll = useCallback(() => {
    const parent = parentRef.current;
    if (!parent) return;
    // A programmatic reading-anchor correction writes scrollTop and fires
    // this handler; ignore it so it can't be read as deliberate movement
    // or re-trigger pagination.
    observedScrollTopRef.current = parent.scrollTop;
    const programmaticOffset = programmaticScrollRef.current;
    programmaticScrollRef.current = null;
    const clampedProgrammaticOffset =
      programmaticOffset === null
        ? null
        : Math.min(programmaticOffset, Math.max(0, parent.scrollHeight - parent.clientHeight));
    if (
      clampedProgrammaticOffset !== null &&
      Math.abs(parent.scrollTop - clampedProgrammaticOffset) < 1
    ) {
      return;
    }
    lastUserScrollAtRef.current = performance.now();
    // The reader moved. The virtualizer would otherwise keep steering
    // toward its last scrollToIndex row for seconds, and a later prepend
    // or shrink moves that row's offset, so it would pull the reader to
    // it. virtual-core 3.14 has no public cancel; clearing its reconcile
    // state writes no scroll position, so momentum is untouched.
    (virtualizer as unknown as { scrollState: unknown }).scrollState = null;
    // Continued momentum emits scroll without another touchmove.
    const anchor = readingAnchorRef.current;
    if (anchor && paginationGeneration > anchor.requestId) {
      userMovedRef.current = true;
    } else if (anchor) {
      const target = firstViewportIntersectingRow(parent, oldestEventId);
      if (target && !userMovedRef.current) {
        readingAnchorRef.current = {
          ...anchor,
          eventId: target.eventId,
          viewportOffset: target.offset,
        };
        anchorCaptureScrollTopRef.current = parent.scrollTop;
        userMovedRef.current = false;
        userInputPendingRef.current = false;
      }
    }
    if (userInputPendingRef.current) {
      userInputPendingRef.current = false;
      if (
        supersedesReadingAnchorOnInput(
          readingAnchorRef.current,
          anchorCaptureScrollTopRef.current,
          parent.scrollTop,
          anchorCorrectionAppliedRef.current,
        )
      ) {
        userMovedRef.current = true;
      }
    }
    readingPointRef.current = viewportRowPoints(parent);
    const distanceFromBottom = parent.scrollHeight - parent.scrollTop - parent.clientHeight;
    const atBottom = distanceFromBottom <= AUTO_SCROLL_THRESHOLD_PX;
    stickToBottomRef.current = atBottom;
    if (atBottom) {
      setFabVisible(false);
      setNewSinceFabShown(0);
      scheduleReadReceipt();
    }
    // Pagination trigger: top of the viewport has crossed the threshold.
    // An active reading anchor means the view is being held, so no second
    // request may race it.
    if (
      parent.scrollTop <= PAGINATION_THRESHOLD_PX &&
      !atStart &&
      !isLoading &&
      !readingAnchorRef.current
    ) {
      const target = firstViewportIntersectingRow(parent, oldestEventId);
      if (target) {
        readingAnchorRef.current = {
          roomId,
          eventId: target.eventId,
          viewportOffset: target.offset,
          requestId: paginationGeneration,
        };
        userMovedRef.current = false;
        anchorCaptureScrollTopRef.current = parent.scrollTop;
        anchorCorrectionAppliedRef.current = false;
        anchorMountAttemptRef.current = null;
        userInputPendingRef.current = false;
      }
      void paginateBackward(PAGE_SIZE);
    }
  }, [
    atStart,
    isLoading,
    oldestEventId,
    paginationGeneration,
    paginateBackward,
    roomId,
    scheduleReadReceipt,
    virtualizer,
  ]);

  // Room switch: reset scroll / counter state so the next room starts
  // from the bottom with fresh refs. Running this before the bottom
  // effects prevents the previous room's reading anchor from
  // applying once the new buffer lands.
  useLayoutEffect(() => {
    hasDoneInitialScrollRef.current = false;
    stickToBottomRef.current = true;
    // Release any anchor held for the previous room and forget a
    // deliberate-movement flag captured mid-pagination. The unread
    // boundary resets via its own read-marker effect keyed on `roomId`.
    readingPointRef.current = [];
    readingAnchorRef.current = null;
    userMovedRef.current = false;
    anchorCaptureScrollTopRef.current = null;
    anchorCorrectionAppliedRef.current = false;
    anchorMountAttemptRef.current = null;
    userInputPendingRef.current = false;
    observedScrollTopRef.current = null;
    programmaticScrollRef.current = null;
    prevEventIdsRef.current = new Set();
    readMarkerRef.current = null;
    lastReceiptEventIdRef.current = null;
    setFabVisible(false);
    setNewSinceFabShown(0);
    setExpandedStateEventIds(new Set());
    setLiveAnnouncement({ slot: "a", text: "" });
  }, [roomId]);

  // Latest read marker delivered by the binding (null when absent).
  const readMarkerRef = useRef<string | null>(null);

  // Read marker → the single `New` separator. Established once from the
  // first placeable marker; frozen thereafter so a later own-receipt
  // advance can't chase it down the screen; reset to unestablished on
  // room change (this effect's dep is `roomId`). When the marker is
  // absent or outside the loaded window `resolveUnreadBoundary`
  // stays unestablished and the boundary stays open rather than guessing.
  useEffect(() => {
    readMarkerRef.current = null;
    setUnreadBoundary({ established: false, eventId: null });
    let cancelled = false;
    const unsub = subscribeReadMarker(client, roomId, (markerEventId) => {
      if (cancelled) return;
      readMarkerRef.current = retainEntryReadMarker(readMarkerRef.current, markerEventId);
      setUnreadBoundary((prev) => {
        if (prev.established) return prev;
        const resolved = resolveUnreadBoundary(
          eventsRef.current,
          isVisibleTimelineEvent,
          readMarkerRef.current,
        );
        if (!resolved.established) return prev;
        return resolved;
      });
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [client, roomId]);

  // Once the loaded window catches up to the marker (e.g. backward
  // pagination brings the marker event into view, or the buffer was
  // still empty when the marker first fired), establish the boundary.
  // Frozen after establishment so own-receipt advances stay put.
  useEffect(() => {
    setUnreadBoundary((prev) => {
      if (prev.established) return prev;
      const resolved = resolveUnreadBoundary(
        eventsRef.current,
        isVisibleTimelineEvent,
        readMarkerRef.current,
      );
      if (!resolved.established) return prev;
      return resolved;
    });
  }, [visibleEvents]);

  // The tail of the gesture that triggered pagination can still emit
  // wheel/touchmove events. Ignore that inertia until the correction is
  // applied, unless the reader scrolls substantially away from capture.
  // Keyboard paging and a direct grab of the scroller are unambiguous user
  // intent (never the tap-inertia tail), so they release the anchor at once.
  useEffect(() => {
    const parent = parentRef.current;
    if (!parent) return;
    const markMoved = () => {
      if (!readingAnchorRef.current) return;
      userInputPendingRef.current = true;
      if (
        supersedesReadingAnchorOnInput(
          readingAnchorRef.current,
          anchorCaptureScrollTopRef.current,
          parent.scrollTop,
          anchorCorrectionAppliedRef.current,
        )
      ) {
        userMovedRef.current = true;
      }
    };
    const markKeyboardIntent = (event: KeyboardEvent) => {
      if (!readingAnchorRef.current) return;
      if (!["PageUp", "PageDown", "Home", "End", "ArrowUp", "ArrowDown"].includes(event.key)) {
        return;
      }
      userInputPendingRef.current = true;
      userMovedRef.current = true;
    };
    // A press whose target is the scroller itself (not a message row) is a
    // deliberate grab; a content tap hits its own element and never fires here.
    const markScrollBarIntent = (event: PointerEvent) => {
      if (!readingAnchorRef.current || event.target !== parent) return;
      userInputPendingRef.current = true;
      userMovedRef.current = true;
    };
    parent.addEventListener("wheel", markMoved, { passive: true });
    parent.addEventListener("touchmove", markMoved, { passive: true });
    const startTouch = () => {
      touchActiveRef.current = true;
    };
    const endTouch = () => {
      touchActiveRef.current = false;
    };
    parent.addEventListener("touchstart", startTouch, { passive: true });
    parent.addEventListener("touchend", endTouch, { passive: true });
    parent.addEventListener("touchcancel", endTouch, { passive: true });
    parent.addEventListener("keydown", markKeyboardIntent);
    parent.addEventListener("pointerdown", markScrollBarIntent);
    return () => {
      parent.removeEventListener("wheel", markMoved);
      parent.removeEventListener("touchmove", markMoved);
      parent.removeEventListener("touchstart", startTouch);
      parent.removeEventListener("touchend", endTouch);
      parent.removeEventListener("touchcancel", endTouch);
      parent.removeEventListener("keydown", markKeyboardIntent);
      parent.removeEventListener("pointerdown", markScrollBarIntent);
    };
  }, []);

  // Initial-position + new-event handling. Runs whenever events or
  // total size change so we can re-pin the pagination anchor after
  // `measureElement` finishes adjusting row heights.
  useLayoutEffect(() => {
    const parent = parentRef.current;
    if (!parent) return;

    // Pin the receipt/FAB baseline when history settles, not on a live event.
    let anchor = readingAnchorRef.current;
    // A short conversation opens near its top, so the first history page
    // takes a reading anchor while the reader sits at the bottom. A new
    // message at the end (the reader's own send included) must still land
    // in view: following the bottom wins over holding a row.
    const newest = visibleEvents[visibleEvents.length - 1];
    if (
      anchor &&
      stickToBottomRef.current &&
      newest &&
      hasDoneInitialScrollRef.current &&
      !prevEventIdsRef.current.has(newest.id)
    ) {
      readingAnchorRef.current = null;
      anchorCaptureScrollTopRef.current = null;
      anchorCorrectionAppliedRef.current = false;
      anchorMountAttemptRef.current = null;
      userInputPendingRef.current = false;
      anchor = null;
    }
    if (
      anchor &&
      paginationGeneration === anchor.requestId &&
      observedScrollTopRef.current !== null &&
      observedScrollTopRef.current !== parent.scrollTop
    ) {
      const target = firstViewportIntersectingRow(parent, oldestEventId);
      if (target && !userMovedRef.current) {
        anchor = { ...anchor, eventId: target.eventId, viewportOffset: target.offset };
        readingAnchorRef.current = anchor;
        anchorCaptureScrollTopRef.current = parent.scrollTop;
        userMovedRef.current = false;
      }
    }
    observedScrollTopRef.current = parent.scrollTop;
    if (anchor) {
      const currentOffset = measureEventOffset(parent, anchor.eventId);
      const decision = resolveReadingAnchor(anchor, {
        roomId,
        currentOffset,
        userMoved: userMovedRef.current,
        settled: paginationGeneration > anchor.requestId,
        outcome: lastPagination,
      });
      // A prepend can cull the reader's row before the page settles. Place
      // it from the virtualizer's offsets in this pass, before paint; the
      // next pass measures it and fine-tunes the correction. Rows above it
      // can remeasure and cull it again, so a new total size places it anew.
      const mountAttempt = `${anchor.requestId}:${rows.length}:${virtualTotalSize}`;
      if (
        currentOffset === null &&
        !decision.release &&
        anchorMountAttemptRef.current !== mountAttempt
      ) {
        const index = rowIndexByEventId.get(anchor.eventId);
        if (index !== undefined) {
          anchorMountAttemptRef.current = mountAttempt;
          const target = virtualizer.getOffsetForIndex(index, "start");
          if (target) {
            parent.scrollTop = target[0] - anchor.viewportOffset;
            observedScrollTopRef.current = parent.scrollTop;
            programmaticScrollRef.current = parent.scrollTop;
          } else {
            virtualizer.scrollToIndex(index, { align: "start" });
          }
        } else if (paginationGeneration > anchor.requestId) {
          readingAnchorRef.current = null;
        }
        return;
      }
      if (decision.correction !== 0) {
        // Measurement can shrink the scroll range before the event arrives.
        parent.scrollTop += decision.correction;
        observedScrollTopRef.current = parent.scrollTop;
        programmaticScrollRef.current = parent.scrollTop;
        if (paginationGeneration > anchor.requestId) {
          anchorCorrectionAppliedRef.current = true;
        }
      }
      if (decision.release) {
        // Pin the FAB baseline to the post-prepend buffer so a later live
        // message isn't miscounted as the prepended history it isn't.
        prevEventIdsRef.current = new Set(visibleEvents.map((e) => e.id));
        readingAnchorRef.current = null;
        anchorCaptureScrollTopRef.current = null;
        anchorCorrectionAppliedRef.current = false;
        anchorMountAttemptRef.current = null;
        userInputPendingRef.current = false;
      }
      return;
    }

    // First paint for this room: jump to the bottom once events are
    // available. `hasDoneInitialScrollRef` keeps this single-shot so
    // we don't re-jump on every subsequent render.
    if (!hasDoneInitialScrollRef.current && visibleEvents.length > 0) {
      virtualizer.scrollToIndex(rows.length - 1, { align: "end" });
      hasDoneInitialScrollRef.current = true;
      stickToBottomRef.current = true;
      scheduleReadReceipt();
      // Rebuild id snapshot so the FAB counter starts from a clean
      // baseline instead of treating every seeded event as new.
      // Seed from the filtered buffer so the first thread reply that
      // lands later is correctly classified as "not new" (it never
      // appears in the feed) rather than firing the FAB.
      prevEventIdsRef.current = new Set(visibleEvents.map((e) => e.id));
      return;
    }

    if (!hasDoneInitialScrollRef.current) return;

    // Steady state: figure out which events are new since the last
    // render. Using an id set instead of "nextCount - prevCount" is
    // robust to backward pagination (prepended events would otherwise
    // inflate the delta). Diffing against the filtered buffer means
    // a thread reply landing in the room buffer never bumps the
    // "N new" FAB counter — the user never sees it in the feed.
    const prevIds = prevEventIdsRef.current;
    // Rows the reader last saw. The first one still mounted is held at
    // its offset, so a reshaped buffer (cache refresh, redaction) never
    // moves the text under the reader or snaps them to the bottom.
    if (!stickToBottomRef.current) {
      const held = readingPointRef.current.some((point) => {
        const offset = measureEventOffset(parent, point.eventId);
        if (offset === null) return false;
        if (Math.abs(offset - point.offset) >= 1) {
          parent.scrollTop += offset - point.offset;
          observedScrollTopRef.current = parent.scrollTop;
          programmaticScrollRef.current = parent.scrollTop;
        }
        return true;
      });
      // Rows a jump left unmounted are captured once they render.
      readingPointRef.current = viewportRowPoints(parent);
      if (held && visibleEvents.length < prevIds.size) {
        prevEventIdsRef.current = new Set(visibleEvents.map((e) => e.id));
        return;
      }
    }
    if (visibleEvents.length > 0 && visibleEvents.length < prevIds.size) {
      virtualizer.scrollToIndex(rows.length - 1, { align: "end" });
      stickToBottomRef.current = true;
      setFabVisible(false);
      setNewSinceFabShown(0);
      scheduleReadReceipt();
      prevEventIdsRef.current = new Set(visibleEvents.map((e) => e.id));
      return;
    }
    const addedMessageEvents: TimelineEvent[] = [];
    for (const ev of visibleEvents) {
      if (!prevIds.has(ev.id) && !isStateEvent(ev)) addedMessageEvents.push(ev);
    }
    if (addedMessageEvents.length === 0) {
      // No new visible messages (e.g. local-echo status flip, only
      // state events arrived, or only thread replies arrived).
      // Refresh the id snapshot and return.
      if (stickToBottomRef.current && !userScrolling() && rows.length > 0) {
        virtualizer.scrollToIndex(rows.length - 1, { align: "end" });
      }
      prevEventIdsRef.current = new Set(visibleEvents.map((e) => e.id));
      return;
    }

    if (stickToBottomRef.current) {
      // Use scrollToIndex so re-measurement of the new message doesn't
      // leave us stranded slightly above the fold.
      virtualizer.scrollToIndex(rows.length - 1, { align: "end" });
      scheduleReadReceipt();
    } else {
      setFabVisible(true);
      setNewSinceFabShown((n) => n + addedMessageEvents.length);
    }
    // Alternate between two summary slots so a repeated announcement
    // ("New message", then "New message" again) produces a real DOM
    // change on at least one of the two live regions. React would
    // otherwise diff the string and skip the update, and the screen
    // reader would stay silent for the second arrival.
    const summary =
      addedMessageEvents.length === 1 ? "New message" : `${addedMessageEvents.length} new messages`;
    setLiveAnnouncement((prev) => {
      const nextSlot = prev.slot === "a" ? "b" : "a";
      return { slot: nextSlot, text: summary };
    });
    prevEventIdsRef.current = new Set(visibleEvents.map((e) => e.id));
  }, [
    visibleEvents,
    oldestEventId,
    rows.length,
    rowIndexByEventId,
    scheduleReadReceipt,
    virtualizer,
    virtualTotalSize,
    virtualItems,
    // Reading-anchor inputs: the effect re-runs when a request settles
    // (generation) or reports its outcome, and against the right room.
    paginationGeneration,
    lastPagination,
    roomId,
    userScrolling,
  ]);

  useEffect(() => {
    // Drop the pending timer when we leave the room or unmount.
    return () => {
      if (readReceiptTimerRef.current) clearTimeout(readReceiptTimerRef.current);
    };
  }, [roomId]);

  // Jump-to-event. When a pending jump for this room arrives:
  //   1. If the event is already in the buffer → scroll to it and
  //      flash.
  //   2. Otherwise, start backward pagination. A follow-up render
  //      (triggered by `events` changing) will re-enter this effect
  //      and find the event, or paginate again until `atStart`.
  //
  // Guarded by `roomId === pendingJump.roomId` because the context
  // also flips the active room; there is a brief render window where
  // a stale jump references a different room (e.g. between the
  // `setActiveRoomIdState(roomId)` and the remount). The jump only
  // makes sense against the right room's buffer.
  useEffect(() => {
    if (!pendingJump) return;
    if (pendingJump.roomId !== roomId) return;
    // Skip if we've already notified the parent this token landed —
    // the parent should clear `pendingJump` in response, but until
    // the next render arrives our prop still carries the handled
    // value.
    if (appliedTokenRef.current === pendingJump.token) return;

    const idx = rowIndexByEventId.get(pendingJump.eventId);
    if (idx !== undefined) {
      // Event is in the buffer. Scroll onto it, flash, and notify the
      // parent. `align: "center"` puts the target in the middle of the
      // viewport, which reads as a clear "here is what you searched
      // for" beat.
      virtualizer.scrollToIndex(idx, { align: "center" });
      stickToBottomRef.current = false;
      setFlashing({ eventId: pendingJump.eventId, token: pendingJump.token });
      appliedTokenRef.current = pendingJump.token;
      onJumpApplied?.();
      return;
    }
    // Event is in the buffer but is a thread reply — unreachable from
    // the main timeline by design. Notify the parent so the jump
    // clears (a future ticket will route these into opening the
    // thread panel; out of scope here per #2570). Without this guard,
    // the paginate-backward path below would spin until `atStart`
    // hunting for an event that the row composer will always filter.
    const targetEvent = eventsRef.current.find((e) => e.id === pendingJump.eventId);
    if (targetEvent && isThreadReply(targetEvent)) {
      appliedTokenRef.current = pendingJump.token;
      onJumpApplied?.();
      return;
    }
    // Event missing — paginate backward. `paginateBackward` is a
    // no-op if the buffer is already at the start; in that case the
    // event is simply unreachable and we still notify the parent so
    // a future re-jump doesn't stall on the same cleared state.
    if (atStart) {
      appliedTokenRef.current = pendingJump.token;
      onJumpApplied?.();
      return;
    }
    if (!isLoading) {
      void paginateBackward(PAGE_SIZE);
    }
  }, [
    pendingJump,
    roomId,
    rowIndexByEventId,
    virtualizer,
    atStart,
    isLoading,
    paginateBackward,
    onJumpApplied,
  ]);

  // Flash clears itself via the row's `onAnimationEnd` handler — see
  // the `data-flash` wrapper in `EventRow`. Keeping the clear in the
  // DOM event path means the duration tracks `--chat-motion-flash`
  // (or the reduced-motion fallback) automatically instead of
  // duplicating the number in JS.

  const draft = useDraft(roomId);

  // Presence, not status, is the delivery signal: the in-flight "sending" flip
  // keeps the id, so an echo clears the draft only once its id leaves the buffer.
  const { markSent: clearDeliveredDraft } = draft;
  const draftValueRef = useRef(draft.value);
  useEffect(() => {
    draftValueRef.current = draft.value;
  }, [draft.value]);
  const seenFailedEchoesRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    const presentIds = new Set(events.map((event) => event.id));
    const draftValue = draftValueRef.current;
    const watched = new Map<string, string>();
    for (const [echoId, body] of seenFailedEchoesRef.current) {
      if (presentIds.has(echoId)) {
        watched.set(echoId, body);
        continue;
      }
      if (draftValue !== null && draftValue === body && body.length > 0) clearDeliveredDraft();
    }
    for (const event of events) {
      if (event.status === "failed" && event.content.type === "m.text") {
        watched.set(event.id, event.content.body);
      }
    }
    seenFailedEchoesRef.current = watched;
  }, [events, clearDeliveredDraft]);

  const focusComposer = useCallback(() => {
    (composerRef ?? quoteComposerRef)?.current?.focus();
  }, [composerRef, quoteComposerRef]);

  const handleCloseEdit = useCallback(
    (eventId: string) => {
      if (editingEventId !== eventId) return;
      const shouldFocusComposer = editOrigin === "recall";
      exitEdit();
      if (shouldFocusComposer) requestAnimationFrame(focusComposer);
    },
    [editOrigin, editingEventId, exitEdit, focusComposer],
  );

  const handleRecallNewer = useCallback(() => {
    const exited = recallNewer();
    if (exited && editOrigin === "recall") requestAnimationFrame(focusComposer);
  }, [editOrigin, focusComposer, recallNewer]);

  const handleRecallOlder = useCallback(() => {
    const result = recallOlder();
    if (shouldPaginateEditRecallBoundary(result, atStart, isLoading)) {
      void paginateBackward(PAGE_SIZE);
    }
  }, [atStart, isLoading, paginateBackward, recallOlder]);

  useLayoutEffect(() => {
    if (editingEventId === null) return;
    const idx = rowIndexByEventId.get(editingEventId);
    if (idx === undefined) return;
    virtualizer.scrollToIndex(idx, { align: "auto" });
  }, [editingEventId, rowIndexByEventId, virtualizer]);

  const gate = useOwnAgentMentionGate(client, roomId);

  const handleSend = useCallback(
    async (
      body: string,
      formattedBody: string | null,
      mentions: string[],
      mentionRoom: boolean,
      meta: ComposerSubmitMeta,
    ) => {
      // Always stick to the bottom after sending so the user sees
      // their own echo.
      stickToBottomRef.current = true;
      await sendText(
        client,
        roomId,
        body,
        formattedBody ?? undefined,
        undefined,
        mentions,
        mentionRoom,
      );
      // Clear the draft only after the homeserver accepts the message,
      // and only when the user hasn't re-edited during the in-flight
      // send — a same-text re-entry must survive. A failed send throws
      // and keeps the typed body for retry.
      if (!meta.editedSinceSubmit()) draft.markSent();
    },
    [client, roomId, draft],
  );

  const handleRetry = useCallback(
    (event: TimelineEvent) => {
      if (event.status !== "failed" || retryInFlightRef.current.has(event.id)) return;
      if (event.content.type !== "m.text") {
        // Media echoes can't be re-uploaded from a stale blob preview
        // (the original bytes aren't kept around) — discard the failed
        // attempt instead of leaving an immortal "Not sent" row.
        if (event.content.type === "m.image" || event.content.type === "m.video") {
          revokeBlobUrl(event.content.url);
          revokeBlobUrl(event.content.thumbnailUrl);
        } else if (event.content.type === "m.file") {
          revokeBlobUrl(event.content.url);
        }
        setDiscardedEchoIds((prev) => (prev.has(event.id) ? prev : new Set(prev).add(event.id)));
        return;
      }
      stickToBottomRef.current = true;
      retryInFlightRef.current.add(event.id);
      void resendLocalEcho(client, roomId, event.id)
        .catch(() => {})
        .finally(() => retryInFlightRef.current.delete(event.id));
    },
    [client, roomId],
  );

  const handleSchedule = useCallback(
    async ({
      body,
      formattedBody,
      sendAt,
    }: {
      body: string;
      formattedBody: string | null;
      sendAt: number;
    }) => {
      if (!userId) {
        pushToast({ tone: "danger", message: "Sign in before scheduling." });
        throw new Error("Sign in before scheduling");
      }
      try {
        await scheduleMessage({
          roomId,
          userId,
          sendAt,
          body,
          formattedBody: formattedBody ?? undefined,
        });
        draft.markSent();
      } catch (err) {
        console.error("schedule message failed", err);
        pushToast({ tone: "danger", message: "Couldn't schedule. Try again." });
        throw err;
      }
    },
    [draft, pushToast, roomId, userId],
  );

  const extraCommands = useAgentCommands(client, roomId);

  // Wire the autocomplete resolver from MatrixContext. The resolver
  // dispatches on the trigger character and forwards joined rooms /
  // user-directory + emoji-mart bindings into the providers. Stable identity — re-creating
  // the closure on every keystroke would cancel the menu's debounce.

  const handleSlashCommand = useCallback(
    async (cmd: SlashCommand, ctx: Pick<SlashCtx, "input" | "argsText">) => {
      const slashCtx: SlashCtx = {
        input: ctx.input,
        argsText: ctx.argsText,
        sendText: async (body) => {
          stickToBottomRef.current = true;
          await sendText(client, roomId, body);
          draft.markSent();
        },
        sendEmote: async (body) => {
          stickToBottomRef.current = true;
          await sendEmote(client, roomId, body);
          draft.markSent();
        },
        sendAgentCommand: async (agentUserId, name, args) => {
          stickToBottomRef.current = true;
          await sendRawMessage(client, roomId, agentCommandContent(agentUserId, name, args));
          draft.markSent();
        },
        setAway: async () => {
          await setPresence(client, "unavailable");
          pushToast({ message: "Presence set to away" });
        },
        openDm: async (userId) => {
          const nextRoomId = await createDirectRoom({ userIds: [userId] });
          setActiveRoomId(nextRoomId);
        },
        inviteUser: async (userId) => {
          await inviteUser(client, roomId, userId);
        },
        openSearch: (query) => {
          onOpenSearch?.(query);
        },
        showToast: (message) => {
          pushToast({ message });
        },
        findDirectRoom,
        setDndUntil,
        setDndOff,
        scheduleReminder: async (sendAt, body) => {
          if (!userId) {
            pushToast({ tone: "danger", message: "Sign in before scheduling." });
            throw new Error("Sign in before scheduling");
          }
          await scheduleMessage({ roomId, userId, sendAt, body });
        },
      };
      await cmd.handler(slashCtx);
    },
    [
      client,
      createDirectRoom,
      draft,
      findDirectRoom,
      onOpenSearch,
      pushToast,
      roomId,
      setActiveRoomId,
      setDndOff,
      setDndUntil,
      userId,
    ],
  );

  // Local user cache for the `@` provider: project the current
  // room's joined members into `UserDirectoryEntry` shape so the
  // first keystroke paints without a directory round-trip. Sorted
  // by power level so admins/moderators surface first — they're
  // the most-mentioned cohort. Memoised on `roomId` so a re-render
  // mid-typing doesn't churn the cache identity (which would cancel
  // the menu's server-side debounce). The cache is a lightweight
  // snapshot at room-mount; #1310 already gives the panel a
  // re-fetch hook for live membership changes, and a stale row
  // heals the moment the server search resolves.
  const localMembers = useMemo<UserDirectoryEntry[]>(() => {
    const members = getMembers(client, roomId);
    return members
      .slice()
      .sort((a, b) => b.powerLevel - a.powerLevel)
      .map((m) => ({
        userId: m.userId,
        displayName: m.displayName || m.userId,
        avatarUrl: m.avatarUrl,
      }));
  }, [client, roomId]);

  const ownAgent = useOwnAgent(client, roomId, userId);
  const ownAgentRef = useRef<OwnAgentSnapshot | null>(null);
  ownAgentRef.current = buildOwnAgentSnapshot(ownAgent);

  const resolveAutocomplete = useCallback<AutocompleteProviderResolver>(
    (trigger, query): AutocompleteProvider => {
      if (trigger === "@") {
        return resolveUserSuggestions({
          query,
          local: localMembers,
          searchUserDirectory,
          allowRoomMention: userId ? canMentionRoom(client, roomId, userId) : false,
          ownAgent: ownAgentRef.current,
        });
      }
      if (trigger === "#") {
        return resolveRoomSuggestions({
          query,
          joinedRooms: rooms.filter((room) => !room.isSpace),
          searchRooms,
        });
      }
      return resolveEmojiSuggestions({ query });
    },
    [rooms, searchUserDirectory, searchRooms, localMembers, client, roomId, userId],
  );
  const handleFiles = useCallback(
    (files: readonly File[]) => {
      // Only force stick-to-bottom when the user is already near the
      // bottom. Forcing it on every upload would yank a scroll-back
      // reader down to the newest message; the existing FAB path
      // already flags new messages for users reading history.
      void upload.uploadFiles(files, roomId);
    },
    [upload, roomId],
  );

  // Desktop (Tauri) drag-drop: the webview swallows HTML5 drag events,
  // so route the OS-native drop through the same `handleFiles` seam and
  // force the DropZone overlay while files hover. No-op on web (#7755).
  const tauriDragActive = useTauriFileDrop(handleFiles);

  const handlePinnedJump = useCallback(
    (eventId: string) => {
      jumpToEvent({ roomId, eventId });
    },
    [jumpToEvent, roomId],
  );

  const handleTogglePin = useCallback(
    async (eventId: string) => {
      try {
        if (isPinned(eventId)) {
          await unpin(eventId);
        } else {
          await pin(eventId);
        }
      } catch (err) {
        console.warn("update pinned messages failed", err);
        pushToast({ message: "Couldn't update pinned messages", tone: "danger" });
      }
    },
    [isPinned, pin, pushToast, unpin],
  );

  const handleStateRunOpenChange = useCallback((events: TimelineEvent[], open: boolean) => {
    setExpandedStateEventIds((prev) => {
      const next = new Set(prev);
      for (const event of events) {
        if (open) next.add(event.id);
        else next.delete(event.id);
      }
      return next;
    });
  }, []);

  const handleFlashEnd = useCallback(() => setFlashing(null), []);

  const onOpenThreadRef = useRef(onOpenThread);
  useEffect(() => {
    onOpenThreadRef.current = onOpenThread;
  }, [onOpenThread]);
  const stableOpenThread = useCallback((root: TimelineEvent) => {
    onOpenThreadRef.current?.(root);
  }, []);

  const handleUnpinAll = useCallback(async () => {
    try {
      await clearPins();
    } catch (err) {
      console.warn("clear pinned messages failed", err);
      pushToast({ message: "Couldn't update pinned messages", tone: "danger" });
      throw err;
    }
  }, [clearPins, pushToast]);

  // Subscribe to typing events for the active room. Clears on room
  // switch so the next room starts with no stale typers. The SDK
  // contract excludes the local user, so `list` is always the set
  // we want to render.
  useEffect(() => {
    setTypingUserIds([]);
    const unsub = subscribeTyping(client, roomId, (list) => {
      setTypingUserIds(list);
    });
    return () => {
      unsub();
    };
  }, [client, roomId]);

  // Subscribe to receipt-change notifications. The callback carries
  // no payload — bumping the epoch triggers a re-render which
  // invalidates `receiptsByEventId` and re-reads fresh snapshots.
  useEffect(() => {
    const unsub = subscribeReceipts(client, roomId, () => {
      setReceiptEpoch((n) => n + 1);
    });
    return () => {
      unsub();
    };
  }, [client, roomId]);

  // Broadcast the local user's typing state. Composer debounces to
  // one `true` call per 10s; this closure just forwards each call to
  // the Matrix wrapper with the 30s server-side timeout.
  const handleTyping = useCallback(
    (typing: boolean) => {
      void matrixSetTyping(client, roomId, typing, typing ? TYPING_TIMEOUT_MS : undefined);
    },
    [client, roomId],
  );

  // Per-room display-name cache. Matrix display names rarely change
  // so we rebuild from the current `events` on each render — the
  // upper bound is one entry per sender in the buffer.
  //
  // Reliance on `events` identity: `useTimeline(client, roomId)`
  // swaps the `events` array on every room switch (it owns the
  // per-room buffer), so a `roomId` change always flows through this
  // memo via a new `events` reference. No separate reset is needed.
  // The ref is synced inside a commit-phase effect below so the ref
  // write never fires during a potentially-discarded render, which
  // matters under concurrent features (transitions, offscreen).
  const nameCache = useMemo(() => {
    const map = new Map<string, string>();
    for (const event of events) {
      const existing = map.get(event.sender.id);
      if (existing !== event.sender.displayName) {
        map.set(event.sender.id, event.sender.displayName);
      }
    }
    return map;
  }, [events]);
  const nameCacheRef = useRef<Map<string, string>>(nameCache);
  // Commit-phase sync keeps the ref aligned with the latest render
  // without mutating refs during render. `resolveTypingName` fires
  // from event handlers and subscription callbacks that run after
  // commit — it never needs the fresh map mid-render.
  useEffect(() => {
    nameCacheRef.current = nameCache;
  }, [nameCache]);

  // Resolve a user id to a display name. Stable identity: the
  // function reads from the ref, so the closure itself never changes
  // across renders. Falls back to the Matrix localpart for users we
  // haven't seen send an event yet.
  const resolveTypingName = useCallback((userId: string): string => {
    const cached = nameCacheRef.current.get(userId);
    if (cached) return cached;
    return matrixLocalpart(userId);
  }, []);

  // Avatar map for read-receipt pills, rebuilt on every timeline update:
  // the member list is often still empty at mount, so a snapshot keyed on
  // the room alone stayed initials-only. Same stable-ref pattern as
  // `resolveTypingName` so the resolver never churns MessageBubble.
  const memberAvatarCache = useMemo(
    () => buildReceiptAvatarMap(events, getMembers(client, roomId)),
    [client, roomId, events],
  );
  const memberAvatarCacheRef = useRef<Map<string, string>>(memberAvatarCache);
  useEffect(() => {
    memberAvatarCacheRef.current = memberAvatarCache;
  }, [memberAvatarCache]);
  const resolveReceiptAvatar = useCallback(
    (userId: string): string | null => memberAvatarCacheRef.current.get(userId) ?? null,
    [],
  );

  const scrollToBottom = useCallback(() => {
    if (rows.length === 0) return;
    virtualizer.scrollToIndex(rows.length - 1, { align: "end" });
    stickToBottomRef.current = true;
    setFabVisible(false);
    setNewSinceFabShown(0);
    scheduleReadReceipt();
  }, [rows.length, scheduleReadReceipt, virtualizer]);

  const activeUpload = upload.uploads.find(
    (record) => record.status === "uploading" || record.status === "sending",
  );
  const progressRows = upload.uploads
    .filter(
      (record) =>
        record.status === "failed" || (record.status === "uploading" && record !== activeUpload),
    )
    .map((record) => (
      <UploadProgressRow
        key={record.id}
        record={record}
        onCancel={() => upload.cancel(record.id)}
        onRetry={() => upload.retry(record.id, roomId)}
      />
    ));

  return (
    <DropZone onDrop={handleFiles} overlayForceVisible={tauriDragActive}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          position: "relative",
        }}
      >
        {onNavigateBack ? (
          <EmbedTimelineHeader title={navigateBackTitle} onBack={onNavigateBack} />
        ) : null}
        {activeMeeting ? <MeetingBanner meeting={activeMeeting} /> : null}
        {replacementRoom ? (
          <RoomMovedBanner onGoToNewRoom={() => setActiveRoomId(replacementRoom)} />
        ) : null}
        <PinnedBanner
          roomId={roomId}
          pins={pins}
          canManage={canManagePins}
          onJump={handlePinnedJump}
          onUnpinAll={handleUnpinAll}
        />
        {archived && <ArchivedTimelineBanner />}
        <div
          style={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            ref={parentRef}
            onScroll={onScroll}
            data-testid="timeline-scroller"
            data-send-list=""
            role="log"
            aria-label="Conversation"
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              // `contain: strict` is the common pattern for virtualised
              // scroll parents — tells the browser our box model is
              // independent of descendants, which keeps the row
              // measurement work cheap.
              contain: "strict",
              // A short conversation sits on the composer, like every chat;
              // once it overflows, `margin-top: auto` resolves to 0 and the
              // virtualiser's offsets are unchanged.
              display: "flex",
              flexDirection: "column",
              opacity: hasRows && (!layoutSettled || !avatarsSettled) ? 0 : undefined,
            }}
          >
            <div
              data-testid="timeline-content"
              style={{
                height: virtualizer.getTotalSize(),
                width: "100%",
                position: "relative",
                marginTop: "auto",
                flexShrink: 0,
              }}
            >
              {virtualItems.map((virtualRow) => {
                const row = rows[virtualRow.index];
                if (!row) return null;
                return (
                  <div
                    key={row.key}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <LongPressable
                      onSwipe={
                        mobile && row.kind === "event" && !row.event.redacted && onOpenThread
                          ? () => onOpenThread(row.event)
                          : undefined
                      }
                      onLongPress={
                        mobile && row.kind === "event" && onLongPressMessage
                          ? (anchor) => onLongPressMessage(row.event, anchor)
                          : undefined
                      }
                    >
                      <RowContent
                        row={row}
                        density={density}
                        roomId={roomId}
                        client={client}
                        onOpenImage={handleOpenImage}
                        receipts={
                          row.kind === "event" ? receiptsByEventId.get(row.event.id) : undefined
                        }
                        resolveName={resolveTypingName}
                        resolveReceiptAvatar={resolveReceiptAvatar}
                        flashing={rowContainsEvent(row, flashing?.eventId)}
                        onFlashEnd={handleFlashEnd}
                        expandedStateEventIds={expandedStateEventIds}
                        onStateRunOpenChange={handleStateRunOpenChange}
                        onOpenThread={onOpenThread ? stableOpenThread : undefined}
                        quoteComposerRef={quoteComposerRef}
                        canManagePins={canManagePins}
                        isPinned={isPinned}
                        onTogglePin={handleTogglePin}
                        archivedReadOnly={archivedReadOnly}
                        editingEventId={editingEventId}
                        onOpenEdit={openEdit}
                        onCloseEdit={handleCloseEdit}
                        onRecallOlder={editOrigin === "recall" ? handleRecallOlder : undefined}
                        onRecallNewer={editOrigin === "recall" ? handleRecallNewer : undefined}
                        mobile={mobile}
                        viewerId={userId}
                        onRetryMessage={handleRetry}
                      />
                    </LongPressable>
                  </div>
                );
              })}
            </div>
          </div>
          {isLoading && (
            <div
              data-testid="timeline-loading"
              style={{ position: "absolute", top: 0, left: 0, right: 0, pointerEvents: "none" }}
            >
              <LoadingRow />
            </div>
          )}
        </div>
        {/* Separate live region so screen readers announce only message
          arrivals, not every virtual-item mount. `aria-atomic` forces
          the SR to read the whole string on each update instead of
          diffing. Two slots (see `liveAnnouncement` state) alternate
          so repeated identical summaries still trigger a DOM change
          and a re-announcement. */}
        <div className="chat-visually-hidden" role="status" aria-live="polite" aria-atomic="true">
          {liveAnnouncement.slot === "a" ? liveAnnouncement.text : ""}
        </div>
        <div className="chat-visually-hidden" role="status" aria-live="polite" aria-atomic="true">
          {liveAnnouncement.slot === "b" ? liveAnnouncement.text : ""}
        </div>
        {fabVisible && <ScrollToBottomFab count={newSinceFabShown} onClick={scrollToBottom} />}
        <TypingIndicator userIds={typingUserIds} resolveName={resolveTypingName} />
        {gate.pending && (
          <AgentMentionPrompt
            busy={gate.pending.busy}
            error={gate.pending.error}
            onAdd={() => gate.choose("add")}
            onSend={() => gate.choose("send")}
            onCancel={gate.cancel}
          />
        )}
        {removedBySelf ? (
          <RemovedComposerBanner removedBySelf={removedBySelf} />
        ) : archivedReadOnly ? (
          <ArchivedComposerBanner />
        ) : replacementRoom ? (
          <MovedRoomComposerBanner />
        ) : (
          <MessageComposer
            ref={composerRef ?? quoteComposerRef}
            client={client}
            mobile={mobile}
            onSend={gate.wrap(handleSend)}
            onSchedule={handleSchedule}
            onTyping={handleTyping}
            placeholder={composerPlaceholder}
            attachButton={
              <FileUploadButton
                onFiles={handleFiles}
                mobile={mobile}
                onError={(message) => pushToast({ message, tone: "danger" })}
              />
            }
            progressRows={progressRows.length > 0 ? progressRows : undefined}
            onPasteFiles={handleFiles}
            value={draft.value}
            onValueChange={draft.setValue}
            resolveAutocomplete={resolveAutocomplete}
            autocompleteEmptyStateMessages={AUTOCOMPLETE_EMPTY_STATE_MESSAGES}
            onSlashCommand={handleSlashCommand}
            extraCommands={extraCommands}
            showToast={(message) => pushToast({ message })}
            onRecallOlder={handleRecallOlder}
            ownAgentGuard={ownAgent.guard}
          />
        )}
        {lightbox}
      </div>
    </DropZone>
  );
}

/**
 * Compact navigation header for the narrow embed. Rendered only when
 * the timeline owns the embed's single column (sub-400 `mode="full"`)
 * and the sidebar is not mounted beside it — the back button is the
 * only path back to the room list there. The wider layouts pass no
 * `onNavigateBack`, so this never renders alongside a visible sidebar.
 */
export function EmbedTimelineHeader({ title, onBack }: { title?: string; onBack: () => void }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        flexShrink: 0,
        height: "var(--chat-sidebar-workspace-header-height)",
        padding: "0 var(--chat-space-3)",
        borderBottom: "1px solid var(--chat-surface-border)",
        background: "var(--chat-surface-app)",
      }}
    >
      <button
        type="button"
        onClick={onBack}
        aria-label="Back to rooms"
        className="chat-button"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          border: "none",
          borderRadius: "var(--chat-radius-sm)",
          background: "transparent",
          color: "var(--chat-fg-secondary)",
          cursor: "pointer",
          flexShrink: 0,
          padding: 0,
        }}
      >
        <ChevronLeft size={16} strokeWidth={ICON_STROKE} aria-hidden />
      </button>
      {title ? (
        <span
          data-feedback-redact="true"
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
          }}
        >
          {title}
        </span>
      ) : null}
    </div>
  );
}

const LONG_PRESS_MS = 450;
const LONG_PRESS_MOVE_TOLERANCE_PX = 10;
const TIMELINE_MESSAGE_ACTIONS_LABEL = "Message actions";
const TIMELINE_REPLY_IN_THREAD_LABEL = "Reply in thread";
const SWIPE_START_PX = 10;
const SWIPE_COMMIT_PX = 56;
const SWIPE_MAX_PX = 88;

/**
 * Wraps a timeline row so a press-and-hold opens the mobile action
 * sheet. A move beyond the tolerance or an early pointer release
 * cancels. When `onLongPress` is undefined (desktop, or a non-event
 * row) the children render bare — no wrapper behaviour. After a
 * long-press fires we swallow the following `click` and `contextmenu`
 * so an underlying control (thread preview, link) and the native menu
 * don't shadow the sheet.
 */
export function LongPressable({
  onLongPress,
  onSwipe,
  children,
}: {
  onLongPress?: (anchor: HTMLElement | null) => void;
  onSwipe?: () => void;
  children: ReactNode;
}) {
  const slideRef = useRef<HTMLDivElement | null>(null);
  const cueRef = useRef<HTMLSpanElement | null>(null);
  const swipingRef = useRef(false);
  const slide = (dx: number, animate: boolean) => {
    const el = slideRef.current;
    if (!el) return;
    el.style.transition = animate ? "transform 180ms cubic-bezier(0.2, 0, 0, 1)" : "none";
    el.style.transform = dx === 0 ? "" : `translateX(${dx}px)`;
    if (cueRef.current) {
      cueRef.current.style.opacity = String(Math.min(1, Math.abs(dx) / SWIPE_COMMIT_PX) * 0.6);
    }
  };
  const timerRef = useRef<number | null>(null);
  const firedRef = useRef(false);
  const originRef = useRef<{ x: number; y: number } | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  if (!onLongPress && !onSwipe) return <>{children}</>;

  return (
    <div
      className={`relative overflow-hidden ${onLongPress ? "chat-lift-row" : ""}`}
      style={onSwipe ? { touchAction: "pan-y" } : undefined}
      onPointerDown={(e) => {
        firedRef.current = false;
        swipingRef.current = false;
        originRef.current = { x: e.clientX, y: e.clientY };
        clearTimer();
        if (!onLongPress) return;
        timerRef.current = window.setTimeout(() => {
          firedRef.current = true;
          onLongPress(slideRef.current);
        }, LONG_PRESS_MS);
      }}
      onPointerMove={(e) => {
        const origin = originRef.current;
        if (!origin) return;
        const dx = e.clientX - origin.x;
        const dy = e.clientY - origin.y;
        if (
          Math.abs(dx) > LONG_PRESS_MOVE_TOLERANCE_PX ||
          Math.abs(dy) > LONG_PRESS_MOVE_TOLERANCE_PX
        ) {
          clearTimer();
        }
        if (!onSwipe) return;
        if (!swipingRef.current && dx < -SWIPE_START_PX && Math.abs(dx) > Math.abs(dy) * 1.5) {
          swipingRef.current = true;
        }
        if (swipingRef.current) {
          // Elastic: full travel up to the commit point, then resistance.
          const pull = Math.min(-dx, SWIPE_COMMIT_PX + (-dx - SWIPE_COMMIT_PX) * 0.3);
          slide(-Math.min(Math.max(pull, 0), SWIPE_MAX_PX), false);
        }
      }}
      onPointerUp={(e) => {
        clearTimer();
        const origin = originRef.current;
        if (swipingRef.current && origin) {
          const committed = origin.x - e.clientX >= SWIPE_COMMIT_PX;
          slide(0, true);
          firedRef.current = true;
          if (committed) onSwipe?.();
        }
        swipingRef.current = false;
        originRef.current = null;
      }}
      onPointerCancel={() => {
        clearTimer();
        if (swipingRef.current) slide(0, true);
        swipingRef.current = false;
        originRef.current = null;
      }}
      onClickCapture={(e) => {
        // A long-press that fired is immediately followed by a click on
        // pointer-up; swallow it so it can't activate a control under
        // the finger (thread preview, link).
        if (firedRef.current) {
          e.preventDefault();
          e.stopPropagation();
          firedRef.current = false;
        }
      }}
      onContextMenu={(e) => {
        if (firedRef.current) e.preventDefault();
      }}
    >
      {onLongPress && (
        <button
          type="button"
          className="chat-visually-hidden"
          onClick={() => onLongPress(slideRef.current)}
        >
          {TIMELINE_MESSAGE_ACTIONS_LABEL}
        </button>
      )}
      {onSwipe && (
        <>
          <button type="button" className="chat-visually-hidden" onClick={onSwipe}>
            {TIMELINE_REPLY_IN_THREAD_LABEL}
          </button>
          <span
            ref={cueRef}
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-muted-foreground"
            style={{ opacity: 0 }}
          >
            <Reply className="size-5" strokeWidth={ICON_STROKE} />
          </span>
        </>
      )}
      <div ref={slideRef} className="chat-lift-content">
        {children}
      </div>
    </div>
  );
}

function rowContainsEvent(row: TimelineRow, eventId: string | undefined): boolean {
  if (eventId === undefined) return false;
  if (row.kind === "event") return row.event.id === eventId;
  if (row.kind === "state-run" || row.kind === "locked-run")
    return row.events.some((event) => event.id === eventId);
  return false;
}

const RowContent = memo(function RowContent({
  row,
  density,
  roomId,
  client,
  onOpenImage,
  receipts,
  resolveName,
  resolveReceiptAvatar,
  flashing,
  onFlashEnd,
  expandedStateEventIds,
  onStateRunOpenChange,
  onOpenThread,
  quoteComposerRef,
  canManagePins,
  isPinned,
  onTogglePin,
  archivedReadOnly,
  editingEventId,
  onOpenEdit,
  onCloseEdit,
  onRecallOlder,
  onRecallNewer,
  mobile,
  onRetryMessage,
  viewerId,
}: {
  row: TimelineRow;
  density: Density;
  roomId: string;
  client: Client;
  onOpenImage: (src: string, alt: string, name: string) => void;
  receipts?: Receipt[];
  resolveName: (userId: string) => string;
  resolveReceiptAvatar?: (userId: string) => string | null;
  flashing: boolean;
  onFlashEnd: () => void;
  expandedStateEventIds: Set<string>;
  onStateRunOpenChange: (events: TimelineEvent[], open: boolean) => void;
  onOpenThread?: (root: TimelineEvent) => void;
  quoteComposerRef?: RefObject<QuoteComposerHandle | null>;
  canManagePins: boolean;
  isPinned: (eventId: string) => boolean;
  onTogglePin: (eventId: string) => Promise<void>;
  archivedReadOnly: boolean;
  editingEventId?: string | null;
  onOpenEdit?: (eventId: string, origin: EditRecallOrigin) => void;
  onCloseEdit?: (eventId: string) => void;
  onRecallOlder?: () => void;
  onRecallNewer?: () => void;
  mobile?: boolean;
  onRetryMessage?: (event: TimelineEvent) => void;
  viewerId?: string | null;
}): ReactNode {
  switch (row.kind) {
    case "event":
      return (
        <EventRow
          event={row.event}
          density={density}
          group={row.group}
          roomId={roomId}
          client={client}
          onOpenImage={onOpenImage}
          receipts={receipts}
          resolveName={resolveName}
          resolveReceiptAvatar={resolveReceiptAvatar}
          flashing={flashing}
          onFlashEnd={onFlashEnd}
          onOpenThread={onOpenThread}
          quoteComposerRef={quoteComposerRef}
          canManagePins={canManagePins}
          isPinned={isPinned}
          onTogglePin={onTogglePin}
          archivedReadOnly={archivedReadOnly}
          editingEventId={editingEventId}
          onOpenEdit={onOpenEdit}
          onCloseEdit={onCloseEdit}
          onRecallOlder={onRecallOlder}
          onRecallNewer={onRecallNewer}
          mobile={mobile}
          onRetryMessage={onRetryMessage}
          viewerId={viewerId}
        />
      );
    case "state-run":
      return (
        <StateEventRunRow
          events={row.events}
          density={density}
          open={row.events.some((event) => expandedStateEventIds.has(event.id))}
          onOpenChange={(open) => onStateRunOpenChange(row.events, open)}
          flashing={flashing}
          onFlashEnd={onFlashEnd}
        />
      );
    case "locked-run":
      return <LockedRunRow events={row.events} density={density} />;
    case "date":
      return <DateSeparator label={row.label} />;
    case "unread-boundary":
      return <UnreadBoundary />;
  }
});

/**
 * Maps a `TimelineEvent` onto the `MessageBubble` component the rest
 * of the chat app uses. Text-shaped events render through the full
 * bubble (reactions, edit, delete, hover actions); richer kinds
 * (image, file, emote, notice) render as their own narrow surfaces
 * since the bubble is text-only in the current codebase. State
 * events collapse to a single muted row.
 *
 * Exported for the routing regression tests under
 * `__tests__/Timeline.fabricated-text.test.tsx` — that suite asserts
 * that unresolvable attachments and undecryptable events never reach
 * `MessageBubble`. Production callers still consume `Timeline`.
 */
export function EventRow({
  event,
  density,
  group,
  roomId,
  client,
  onOpenImage,
  receipts,
  resolveName,
  resolveReceiptAvatar,
  flashing,
  onFlashEnd,
  onOpenThread,
  quoteComposerRef,
  canManagePins,
  isPinned,
  onTogglePin,
  archivedReadOnly,
  editingEventId,
  onOpenEdit,
  onCloseEdit,
  onRecallOlder,
  onRecallNewer,
  mobile,
  onRetryMessage,
  viewerId,
}: {
  event: TimelineEvent;
  density: Density;
  group: "first" | "middle" | "last" | "single";
  roomId: string;
  client: Client;
  onOpenImage: (src: string, alt: string, name: string) => void;
  receipts: Receipt[] | undefined;
  resolveName: (userId: string) => string;
  resolveReceiptAvatar?: (userId: string) => string | null;
  flashing: boolean;
  onFlashEnd: () => void;
  onOpenThread?: (root: TimelineEvent) => void;
  quoteComposerRef?: RefObject<QuoteComposerHandle | null>;
  canManagePins: boolean;
  isPinned: (eventId: string) => boolean;
  onTogglePin: (eventId: string) => Promise<void>;
  archivedReadOnly: boolean;
  editingEventId?: string | null;
  onOpenEdit?: (eventId: string, origin: EditRecallOrigin) => void;
  onCloseEdit?: (eventId: string) => void;
  onRecallOlder?: () => void;
  onRecallNewer?: () => void;
  mobile?: boolean;
  onRetryMessage?: (event: TimelineEvent) => void;
  viewerId?: string | null;
}) {
  const c: TimelineEventContent = event.content;
  const content = renderEventBody(
    event,
    c,
    density,
    group,
    roomId,
    client,
    onOpenImage,
    receipts,
    resolveName,
    resolveReceiptAvatar,
    onOpenThread,
    quoteComposerRef,
    canManagePins,
    isPinned,
    onTogglePin,
    archivedReadOnly,
    editingEventId,
    onOpenEdit,
    onCloseEdit,
    onRecallOlder,
    onRecallNewer,
    mobile,
    onRetryMessage,
    viewerId,
  );
  // The wrapper is always rendered so toggling `data-flash` is a
  // no-op on the DOM tree structure — only an attribute flips. That
  // preserves React keys, virtualiser measurement state, and any
  // focus/selection inside the row. The CSS `[data-flash="1"]`
  // selector kicks off the fade; `onAnimationEnd` clears the
  // attribute so a repeat jump replays cleanly. The `animationName`
  // guard ensures a nested animation (future reaction pop, edit
  // fade, image-loaded transition) can't bubble and prematurely
  // clear the flash.
  const onAnimationEnd = (e: ReactAnimationEvent<HTMLDivElement>) => {
    if (
      e.animationName === "chat-highlight-flash" ||
      e.animationName === "chat-highlight-flash-reduced"
    ) {
      onFlashEnd();
    }
  };
  const standalonePinActions: MessageAction[] =
    canManagePins &&
    c.type !== "m.text" &&
    !isStateEvent(event) &&
    !isUndecryptableTimelineEvent(event) &&
    !isChromedMediaEvent(event, c)
      ? [
          buildPinAction({
            isPinned: isPinned(event.id),
            onToggle: () => {
              void onTogglePin(event.id);
            },
          }),
        ]
      : [];
  return (
    <div
      className={standalonePinActions.length > 0 ? "chat-standalone-pin-row" : undefined}
      data-flash={flashing ? "1" : undefined}
      onAnimationEnd={flashing ? onAnimationEnd : undefined}
      style={{ position: "relative" }}
    >
      {content}
      {standalonePinActions.length > 0 && (
        <div
          className="chat-standalone-pin-action"
          style={{
            position: "absolute",
            top: "var(--chat-space-1)",
            right: "var(--chat-space-4)",
          }}
        >
          <MessageActions actions={standalonePinActions} />
        </div>
      )}
    </div>
  );
}

function baseTimelineMessage(
  event: TimelineEvent,
  roomId: string,
  group: "first" | "middle" | "last" | "single",
  receipts: Receipt[] | undefined,
  body: string,
  status: MessageEncryptionStatus | undefined,
): TimelineMessage {
  return {
    id: event.id,
    roomId,
    sender: {
      id: event.sender.id,
      name: event.sender.displayName,
      avatarUrl: event.sender.avatarUrl,
    },
    time: formatTimeOfDay(event.ts),
    ts: event.ts,
    body,
    editedAt: event.editedAt,
    redacted: event.redacted,
    reactions: event.reactions,
    content: event.content,
    actionResponses: event.actionResponses,
    status,
    deliveryStatus: event.status,
    uploadProgress: event.uploadProgress,
    group,
    receipts,
    // Derive "this row is a thread root" from the SDK-bundled
    // aggregation. `MessageBubble` uses this to gate `useThread`'s
    // `enabled` flag so non-roots never subscribe — see #2653.
    isThreadRoot: (event.threadSummary?.replyCount ?? 0) > 0,
    threadReplyCount: event.threadSummary?.replyCount,
  };
}

function renderEventBody(
  event: TimelineEvent,
  c: TimelineEventContent,
  density: Density,
  group: "first" | "middle" | "last" | "single",
  roomId: string,
  client: Client,
  onOpenImage: (src: string, alt: string, name: string) => void,
  receipts: Receipt[] | undefined,
  resolveName: (userId: string) => string,
  resolveReceiptAvatar: ((userId: string) => string | null) | undefined,
  onOpenThread?: (root: TimelineEvent) => void,
  quoteComposerRef?: RefObject<QuoteComposerHandle | null>,
  canManagePins?: boolean,
  isPinned?: (eventId: string) => boolean,
  onTogglePin?: (eventId: string) => Promise<void>,
  archivedReadOnly = false,
  editingEventId: string | null = null,
  onOpenEdit?: (eventId: string, origin: EditRecallOrigin) => void,
  onCloseEdit?: (eventId: string) => void,
  onRecallOlder?: () => void,
  onRecallNewer?: () => void,
  mobile = false,
  onRetryMessage?: (event: TimelineEvent) => void,
  viewerId?: string | null,
): ReactNode {
  const retry = onRetryMessage ? () => onRetryMessage(event) : undefined;
  const editProps: Pick<
    ComponentProps<typeof MessageBubble>,
    "editing" | "onEditingChange" | "onRecallOlder" | "onRecallNewer"
  > = onOpenEdit && onCloseEdit
    ? {
        editing: event.id === editingEventId,
        onEditingChange: (next, origin) => {
          if (next) onOpenEdit(event.id, origin ?? "manual");
          else onCloseEdit(event.id);
        },
        onRecallOlder,
        onRecallNewer,
      }
    : {};
  const pinActions: MessageAction[] = canManagePins
    ? [
        buildPinAction({
          isPinned: isPinned?.(event.id) ?? false,
          onToggle: () => {
            void onTogglePin?.(event.id);
          },
        }),
      ]
    : [];

  if (isUndecryptableTimelineEvent(event)) {
    const message = baseTimelineMessage(event, roomId, group, receipts, "", "undecryptable");
    return (
      <MessageBubble
        message={message}
        density={density}
        resolveReceiptName={resolveName}
        resolveReceiptAvatar={resolveReceiptAvatar}
        onRequestKeys={(requestRoomId, eventId) =>
          requestKeysForEvent(client, requestRoomId, eventId)
        }
        onOpenThread={onOpenThread ? () => onOpenThread(event) : undefined}
        quoteComposerRef={quoteComposerRef}
        archivedReadOnly={archivedReadOnly}
        mobile={mobile}
        {...editProps}
      />
    );
  }
  if (isStateEvent(event)) {
    return <StateEventRow event={event} density={density} group={group} viewerId={viewerId} />;
  }
  const displayBody = messageContentBody(c) ?? "";
  if (c.type === "m.text" || c.type === "m.notice" || c.type === "m.emote") {
    const message: TimelineMessage = {
      ...baseTimelineMessage(event, roomId, group, receipts, displayBody, event.encryptionStatus),
      mentions: messageMentions(c),
      variant: c.type === "m.notice" ? "notice" : c.type === "m.emote" ? "emote" : undefined,
    };
    // MessageBubble's `onOpenThread` reports back by message id; the
    // event is already in scope here, so we resolve id → event in the
    // same closure rather than asking the parent (or context) to
    // re-look-up an event we already hold.
    const handleOpenThread = onOpenThread ? () => onOpenThread(event) : undefined;
    return (
      <MessageBubble
        message={message}
        density={density}
        resolveReceiptName={resolveName}
        resolveReceiptAvatar={resolveReceiptAvatar}
        onRequestKeys={(requestRoomId, eventId) =>
          requestKeysForEvent(client, requestRoomId, eventId)
        }
        onOpenThread={handleOpenThread}
        quoteComposerRef={quoteComposerRef}
        extraActions={pinActions}
        archivedReadOnly={archivedReadOnly}
        mobile={mobile}
        onRetry={retry}
        {...editProps}
      />
    );
  }
  // UTD-on-media plumbing. Lets the user trigger `requestKeysForEvent`
  // from the media row when an `m.image` / `m.video` / `m.file`
  // arrives with `encryptionStatus === "undecryptable"`.
  if (
    event.encryptionStatus === "undecryptable" &&
    (c.type === "m.image" || c.type === "m.video" || c.type === "m.file" || c.type === "m.audio")
  ) {
    return (
      <MediaRow event={event} density={density} group={group}>
        <AttachmentUnavailable
          label={mediaUnavailableLabel(c.type, displayBody)}
          onRequestKeys={() => requestKeysForEvent(client, roomId, event.id)}
        />
      </MediaRow>
    );
  }
  if (c.type !== "m.image" && c.type !== "m.video" && c.type !== "m.audio" && c.type !== "m.file") {
    return <StateEventRow event={event} density={density} group={group} viewerId={viewerId} />;
  }
  if (!mediaSource(c)) {
    return (
      <MediaRow event={event} density={density} group={group}>
        <AttachmentUnavailable label={mediaUnavailableLabel(c.type, displayBody)} />
      </MediaRow>
    );
  }
  const message: TimelineMessage = {
    ...baseTimelineMessage(event, roomId, group, receipts, displayBody, event.encryptionStatus),
    mediaBody: <MessageMediaBody client={client} event={event} onOpenImage={onOpenImage} />,
  };
  return (
    <MessageBubble
      message={message}
      density={density}
      resolveReceiptName={resolveName}
      resolveReceiptAvatar={resolveReceiptAvatar}
      onRequestKeys={(requestRoomId, eventId) =>
        requestKeysForEvent(client, requestRoomId, eventId)
      }
      onOpenThread={onOpenThread ? () => onOpenThread(event) : undefined}
      quoteComposerRef={quoteComposerRef}
      extraActions={pinActions}
      archivedReadOnly={archivedReadOnly}
      mobile={mobile}
      onRetry={retry}
      retryLabel="Discard"
      {...editProps}
    />
  );
}

type MediaContent = Extract<
  TimelineEvent["content"],
  { type: "m.image" | "m.video" | "m.audio" | "m.file" }
>;

/** Whether the event is an image, video, audio or file message. */
export function isMediaEvent(event: TimelineEvent): boolean {
  const type = event.content.type;
  return type === "m.image" || type === "m.video" || type === "m.audio" || type === "m.file";
}

function mediaUnavailableLabel(type: MediaContent["type"], name: string): string {
  if (name) return name;
  if (type === "m.image") return "Image unavailable";
  if (type === "m.video") return "Video unavailable";
  if (type === "m.audio") return "Audio unavailable";
  return "Attachment unavailable";
}

export function mediaSource(c: MediaContent): MatrixMediaSource | null {
  return c.source ?? mediaSourceFromMxc(c.url);
}

/** The picture, player or download inside a media message — shared by the room and threads. */
export function MessageMediaBody({
  client,
  event,
  onOpenImage,
}: {
  client: Client;
  event: TimelineEvent;
  onOpenImage: (src: string, alt: string, name: string) => void;
}) {
  const c = event.content;
  if (c.type !== "m.image" && c.type !== "m.video" && c.type !== "m.audio" && c.type !== "m.file") {
    return null;
  }
  const name = messageContentBody(c) ?? "";
  const unavailableLabel = mediaUnavailableLabel(c.type, name);
  const source = mediaSource(c);
  if (!source) return <AttachmentUnavailable label={unavailableLabel} />;
  const download = (
    <DownloadMedia
      client={client}
      source={source}
      name={name}
      mimetype={c.mimetype}
      size={c.size}
      unavailableLabel={unavailableLabel}
    />
  );
  if (c.type === "m.image") {
    return (
      <ImageMedia
        client={client}
        source={source}
        thumbnailSource={c.thumbnailSource ?? mediaSourceFromMxc(c.thumbnailUrl)}
        mimetype={c.mimetype}
        alt={name}
        width={c.width}
        height={c.height}
        onOpenImage={onOpenImage}
      />
    );
  }
  if (c.type === "m.video") {
    return (
      <VideoMedia
        client={client}
        source={source}
        mimetype={c.mimetype}
        thumbnailSource={c.thumbnailSource ?? mediaSourceFromMxc(c.thumbnailUrl)}
        thumbnailMimetype={c.thumbnailInfo?.mimetype}
        width={c.width}
        height={c.height}
        fallback={download}
      />
    );
  }
  if (c.type === "m.audio") {
    return <AudioMedia client={client} source={source} mimetype={c.mimetype} fallback={download} />;
  }
  return download;
}

function ImageMedia({
  client,
  source,
  thumbnailSource,
  mimetype,
  alt,
  width,
  height,
  onOpenImage,
}: {
  client: Client;
  source: MatrixMediaSource;
  thumbnailSource?: MatrixMediaSource | null;
  mimetype?: string;
  alt: string;
  width?: number;
  height?: number;
  onOpenImage: (src: string, alt: string, name: string) => void;
}) {
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);
  const thumbnailOpts = useMemo(
    () => ({ thumbnail: { w: 800, h: 800, method: "scale" as const } }),
    [],
  );
  const preview = useMediaObjectUrl(client, thumbnailSource ?? source, mimetype, thumbnailOpts);
  return (
    <MediaPreview
      alt={alt}
      width={width}
      height={height}
      unavailable={preview.status === "failed"}
      thumbnailSrc={preview.status === "ready" ? preview.src : undefined}
      onOpen={() => {
        void (async () => {
          try {
            const { bytes } = await client.bindings.downloadMedia(source);
            const url = objectUrlFromBytes(bytes, mimetype);
            if (!aliveRef.current) {
              URL.revokeObjectURL(url);
              return;
            }
            onOpenImage(url, alt, alt);
          } catch (err) {
            console.warn("Matrix media download failed", err);
            // The inline preview has already proven useful; failed full-size
            // fetches simply keep the lightbox closed.
          }
        })();
      }}
    />
  );
}

function DownloadMedia({
  client,
  source,
  name,
  mimetype,
  size,
  unavailableLabel,
}: {
  client: Client;
  source: MatrixMediaSource;
  name: string;
  mimetype?: string;
  size?: number;
  unavailableLabel: string;
}) {
  const mountedRef = useRef(true);
  const objectUrlRef = useRef<string | null>(null);
  const key = mediaSourceKey(source);
  const keyRef = useRef(key);
  const [state, setState] = useState<
    | { status: "idle" }
    | { status: "preparing" }
    | { status: "ready"; url: string }
    | { status: "failed" }
  >({ status: "idle" });
  const stableSource = useStableMediaSource(source, key);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);
  useLayoutEffect(() => {
    keyRef.current = key;
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setState({ status: "idle" });
  }, [key]);
  const handleDownload = useCallback(() => {
    if (state.status === "preparing") return;
    if (state.status === "ready") return;
    setState({ status: "preparing" });
    const downloadKey = key;
    void (async () => {
      try {
        const media = await client.bindings.downloadMedia(stableSource);
        const url = objectUrlFromBytes(media.bytes, mimetype);
        if (!mountedRef.current || keyRef.current !== downloadKey) {
          URL.revokeObjectURL(url);
          return;
        }
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = url;
        setState({ status: "ready", url });
        triggerBrowserDownload(url, name);
      } catch (err) {
        console.warn("Matrix media download failed", err);
        if (mountedRef.current && keyRef.current === downloadKey) setState({ status: "failed" });
      }
    })();
  }, [client, key, mimetype, name, stableSource, state.status]);
  if (state.status === "failed") return <AttachmentUnavailable label={unavailableLabel} />;
  return (
    <FileAttachment
      name={name}
      href={state.status === "ready" ? state.url : undefined}
      onDownload={state.status === "ready" ? undefined : handleDownload}
      busy={state.status === "preparing"}
      mimetype={mimetype}
      size={size}
    />
  );
}

export function triggerBrowserDownload(url: string, name: string) {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name || "attachment";
  anchor.rel = "noopener noreferrer";
  anchor.style.display = "none";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

function mediaSourceFromMxc(mxcUri: string | undefined): MatrixMediaSource | null {
  if (typeof mxcUri !== "string" || !mxcUri.startsWith("mxc://")) return null;
  return { url: mxcUri };
}

// Test seam — exported under `__timelineTestUtils` below, not consumed
// by other production modules.
function AttachmentUnavailable({
  label,
  onRequestKeys,
}: {
  label: string;
  /** When present, render a "Request keys" affordance next to the
   *  unavailable chip. Used by media UTD rows — same copy and state
   *  machine as the text-row `UtdPlaceholder` button. */
  onRequestKeys?: () => Promise<void>;
}) {
  const { cryptoReady, cryptoError, retryCryptoBootstrap } = useMatrix();
  const failedCrypto = !cryptoReady && cryptoError !== null;
  const displayLabel = failedCrypto ? "Encryption setup failed — tap to retry" : label;
  const style: CSSProperties = {
    display: "inline-flex",
    maxWidth: "var(--chat-file-attachment-max-width)",
    padding: "var(--chat-space-2) var(--chat-space-3)",
    borderRadius: "var(--chat-radius-md)",
    background: "var(--chat-surface-raised)",
    color: failedCrypto ? "var(--chat-state-danger)" : "var(--chat-fg-muted)",
    fontFamily: "var(--chat-font-sans)",
    fontSize: "var(--chat-type-body-sm-size)",
    lineHeight: "var(--chat-type-body-sm-line)",
  };
  if (failedCrypto) {
    return (
      <button
        type="button"
        className="chat-button"
        style={{
          ...style,
          border: "none",
          cursor: "pointer",
        }}
        onClick={() => void retryCryptoBootstrap()}
      >
        {displayLabel}
      </button>
    );
  }
  if (onRequestKeys) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--chat-space-3)" }}>
        <span style={style}>{displayLabel}</span>
        <RequestKeysButton onRequestKeys={onRequestKeys} />
      </span>
    );
  }
  return <span style={style}>{displayLabel}</span>;
}

/* ─── rich-content surfaces (unchanged visual language) ────────── */

function StateEventRow({
  event,
  density,
  group,
  viewerId,
}: {
  event: TimelineEvent;
  density: Density;
  group: "first" | "middle" | "last" | "single";
  viewerId?: string | null;
}) {
  const label = stateEventLabel(event, { viewerId });
  if (label === null) return null;
  const avatarCol =
    density === "compact"
      ? "var(--chat-timeline-avatar-column-compact)"
      : "var(--chat-timeline-avatar-column-comfortable)";
  const gap = density === "compact" ? "var(--chat-space-2)" : "var(--chat-space-3)";
  const groupGap =
    group === "first" || group === "single"
      ? "var(--chat-timeline-group-gap)"
      : "var(--chat-timeline-within-group-gap)";
  return (
    <div
      data-event-id={event.id}
      style={{
        padding: `${groupGap} var(--chat-space-4) 0 calc(${avatarCol} + ${gap} + var(--chat-timeline-state-row-inset))`,
        color: "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
      }}
    >
      {label}
    </div>
  );
}

const LOCKED_RUN_DATE: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };

function isHistoryNotShared(event: TimelineEvent): boolean {
  return event.utdWithheldCode === "m.history_not_shared";
}

function LockedRunRow({ events, density }: { events: TimelineEvent[]; density: Density }) {
  const { canUnlockHistory, resumeRecovery } = useMatrix();
  const first = events[0];
  const last = events[events.length - 1];
  if (!first || !last) return null;
  const avatarCol =
    density === "compact"
      ? "var(--chat-timeline-avatar-column-compact)"
      : "var(--chat-timeline-avatar-column-comfortable)";
  const gap = density === "compact" ? "var(--chat-space-2)" : "var(--chat-space-3)";
  const count = events.length;
  const notShared = events.every(isHistoryNotShared);
  const summary = notShared
    ? count === 1
      ? "1 message from before you joined wasn't shared with you."
      : `${count} messages from before you joined weren't shared with you.`
    : count === 1
      ? "1 message is locked on this device."
      : `${count} messages are locked on this device.`;
  const range = isSameDay(first.ts, last.ts)
    ? null
    : `${new Date(first.ts).toLocaleDateString(undefined, LOCKED_RUN_DATE)} – ${new Date(
        last.ts,
      ).toLocaleDateString(undefined, LOCKED_RUN_DATE)}`;
  const utdReasons = [
    ...new Set(events.map((event) => event.utdWithheldCode ?? event.utdReason ?? "unknown")),
  ]
    .sort((a, b) => a.localeCompare(b))
    .join(" ");
  return (
    <div
      data-event-id={first.id}
      data-utd-reasons={utdReasons || undefined}
      data-chat-locked-run
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: "var(--chat-space-2)",
        padding: `var(--chat-timeline-group-gap) var(--chat-space-4) 0 calc(${avatarCol} + ${gap} + var(--chat-timeline-state-row-inset))`,
        color: "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
      }}
    >
      <Lock aria-hidden size={14} strokeWidth={ICON_STROKE} style={{ flexShrink: 0 }} />
      <span>{summary}</span>
      {range && <span>{range}</span>}
      {canUnlockHistory && !notShared && (
        <button
          type="button"
          onClick={resumeRecovery}
          className="chat-button outline-none focus-visible:shadow-neon-cyan"
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            minHeight: 24,
            color: "var(--chat-fg-primary)",
            font: "inherit",
            textDecoration: "underline",
            textUnderlineOffset: 3,
            cursor: "pointer",
          }}
        >
          Unlock
        </button>
      )}
    </div>
  );
}

function StateEventRunRow({
  events,
  density,
  open,
  onOpenChange,
  flashing,
  onFlashEnd,
}: {
  events: TimelineEvent[];
  density: Density;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flashing: boolean;
  onFlashEnd: () => void;
}) {
  const first = events[0];
  useEffect(() => {
    if (flashing && !open) onOpenChange(true);
  }, [flashing, onOpenChange, open]);
  if (!first) return null;
  const last = events[events.length - 1] ?? first;
  const labels = events.map((event) => ({
    event,
    label: stateEventLabel(event) ?? `${event.type} updated`,
  }));
  const avatarCol =
    density === "compact"
      ? "var(--chat-timeline-avatar-column-compact)"
      : "var(--chat-timeline-avatar-column-comfortable)";
  const gap = density === "compact" ? "var(--chat-space-2)" : "var(--chat-space-3)";
  const padding = `var(--chat-timeline-group-gap) var(--chat-space-4) 0 calc(${avatarCol} + ${gap} + var(--chat-timeline-state-row-inset))`;
  const summary = `${events.length} room updates`;
  const timeLabel =
    last.ts !== first.ts
      ? `${formatMessageTime(first.ts)} to ${formatMessageTime(last.ts)}`
      : formatMessageTime(first.ts);
  const onAnimationEnd = (e: ReactAnimationEvent<HTMLDivElement>) => {
    if (
      e.animationName === "chat-highlight-flash" ||
      e.animationName === "chat-highlight-flash-reduced"
    ) {
      onFlashEnd();
    }
  };
  const toggle = () => onOpenChange(!open);
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    toggle();
  };
  return (
    <div
      data-event-id={first.id}
      data-flash={flashing ? "1" : undefined}
      onAnimationEnd={flashing ? onAnimationEnd : undefined}
      style={{ padding }}
    >
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={onKeyDown}
        className="chat-button"
        style={{
          display: "inline-flex",
          alignItems: "baseline",
          gap: "var(--chat-space-2)",
          padding: 0,
          border: "none",
          background: "transparent",
          color: "var(--chat-fg-muted)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
          cursor: "pointer",
          width: "fit-content",
        }}
      >
        <span>{summary}</span>
        <span style={{ color: "var(--chat-fg-muted)", fontSize: "var(--chat-type-body-xs-size)" }}>
          {timeLabel}
        </span>
      </div>
      {open && (
        <ul
          aria-label={`${events.length} room updates, expanded`}
          style={{
            margin: "var(--chat-space-1) 0 0",
            padding: 0,
            listStyle: "none",
            color: "var(--chat-fg-muted)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
          }}
        >
          {labels.map(({ event, label }) => (
            <li key={event.id} data-event-id={event.id}>
              {label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MediaRow({
  event,
  density,
  group,
  children,
}: {
  event: TimelineEvent;
  density: Density;
  group: "first" | "middle" | "last" | "single";
  children: ReactNode;
}) {
  const avatarCol =
    density === "compact"
      ? "var(--chat-timeline-avatar-column-compact)"
      : "var(--chat-timeline-avatar-column-comfortable)";
  const gap = density === "compact" ? "var(--chat-space-2)" : "var(--chat-space-3)";
  const groupGap =
    group === "first" || group === "single"
      ? "var(--chat-timeline-group-gap)"
      : "var(--chat-timeline-within-group-gap)";
  return (
    <div
      data-event-id={event.id}
      style={{
        display: "grid",
        gridTemplateColumns: `${avatarCol} 1fr`,
        gap,
        padding: `${groupGap} var(--chat-space-4) 0 var(--chat-space-4)`,
      }}
    >
      <span aria-hidden />
      <div data-feedback-redact="true" style={{ minWidth: 0 }}>
        {children}
      </div>
    </div>
  );
}

function DateSeparator({ label }: { label: string }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        padding: "var(--chat-space-4) var(--chat-space-4)",
      }}
    >
      <span
        style={{
          color: "var(--chat-fg-muted)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-xs-size)",
          lineHeight: "var(--chat-type-body-xs-line)",
        }}
      >
        {label}
      </span>
    </div>
  );
}

function LoadingRow() {
  return (
    <div
      style={{
        padding: "var(--chat-space-3) var(--chat-space-4)",
        display: "flex",
        justifyContent: "center",
        color: "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
      }}
      role="status"
      aria-label="Loading earlier messages"
    >
      <Spinner />
      <span style={{ marginLeft: "var(--chat-space-2)" }}>Loading earlier messages…</span>
    </div>
  );
}

// The single `New` reading cue. Rendered from its own row (never a
// timeline event) so it is excluded from message totals and from reader
// grouping; keyed `unread:<eventId>`. Reuses the chat tokens — no new
// theme values.
function UnreadBoundary() {
  return (
    <div
      data-cy="chat-unread-boundary"
      data-testid="unread-boundary"
      style={{
        display: "flex",
        justifyContent: "center",
        padding: "var(--chat-space-3) var(--chat-space-4)",
      }}
    >
      <span
        style={{
          color: "var(--chat-fg-muted)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-xs-size)",
          lineHeight: "var(--chat-type-body-xs-line)",
        }}
      >
        New
      </span>
    </div>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="chat-timeline-spinner"
      style={{
        width: "var(--chat-timeline-spinner-size)",
        height: "var(--chat-timeline-spinner-size)",
        borderRadius: "var(--chat-radius-full)",
        border: "var(--chat-timeline-spinner-border) solid var(--chat-surface-raised)",
        borderTopColor: "var(--chat-fg-muted)",
        display: "inline-block",
      }}
    />
  );
}

function ScrollToBottomFab({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={count > 0 ? `Scroll to latest, ${count} new messages` : "Scroll to latest"}
      className="chat-button"
      style={{
        position: "absolute",
        right: "var(--chat-timeline-fab-right)",
        bottom: "var(--chat-timeline-fab-bottom)",
        width: "var(--chat-timeline-fab-size)",
        height: "var(--chat-timeline-fab-size)",
        borderRadius: "var(--chat-radius-full)",
        background: "var(--chat-surface-overlay)",
        border: "1px solid var(--chat-surface-border)",
        color: "var(--chat-fg-primary)",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
      }}
    >
      <ChevronDownGlyph />
      {count > 0 && (
        <span
          style={{
            position: "absolute",
            top: "calc(var(--chat-timeline-fab-badge-offset) * -1)",
            right: "calc(var(--chat-timeline-fab-badge-offset) * -1)",
            minWidth: "var(--chat-timeline-fab-badge-min-width)",
            height: "var(--chat-timeline-fab-badge-height)",
            padding: "0 var(--chat-timeline-fab-badge-padding-inline)",
            borderRadius: "var(--chat-radius-full)",
            background: "var(--chat-unread-dot)",
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-xs-size)",
            lineHeight: "var(--chat-type-body-xs-line)",
            fontWeight: 300,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}

function RoomMovedBanner({ onGoToNewRoom }: { onGoToNewRoom: () => void }) {
  return (
    <div
      style={{
        height: 48,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        padding: "0 var(--chat-space-4)",
        background: "var(--chat-surface-raised)",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
      }}
    >
      <ArrowUpRight
        aria-hidden
        size={20}
        strokeWidth={ICON_STROKE}
        style={{ color: "var(--chat-fg-secondary)", flexShrink: 0 }}
      />
      <span
        style={{
          fontSize: "var(--chat-type-body-md-size)",
          lineHeight: "var(--chat-type-body-md-line)",
          fontWeight: 500,
        }}
      >
        This room has been replaced.
      </span>
      <button
        type="button"
        onClick={onGoToNewRoom}
        style={{
          height: 32,
          marginLeft: "auto",
          padding: "0 var(--chat-space-3)",
          border: "none",
          borderRadius: "var(--chat-radius-md)",
          background: "var(--chat-cta-bg)",
          color: "var(--chat-cta-fg)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
          fontWeight: 500,
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        Go to the new room
      </button>
    </div>
  );
}

function ArchivedTimelineBanner() {
  return (
    <div
      style={{
        height: 32,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        padding: "0 var(--chat-space-4)",
        background: "transparent",
        color: "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
      }}
    >
      <ArchiveGlyph />
      <span>This channel is archived.</span>
    </div>
  );
}

function ArchivedComposerBanner() {
  return (
    <div
      role="status"
      style={{
        height: 48,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        padding: "0 var(--chat-space-4)",
        margin: "var(--chat-composer-padding)",
        background: "transparent",
        color: "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-md-size)",
        lineHeight: "var(--chat-type-body-md-line)",
      }}
    >
      This channel is archived.
    </div>
  );
}

function MovedRoomComposerBanner() {
  return (
    <div
      role="status"
      style={{
        height: 48,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        padding: "0 var(--chat-space-4)",
        margin: "var(--chat-composer-padding)",
        background: "transparent",
        color: "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-md-size)",
        lineHeight: "var(--chat-type-body-md-line)",
      }}
    >
      This room has been replaced. You can’t send messages here.
    </div>
  );
}

function RemovedComposerBanner({ removedBySelf }: { removedBySelf: RemovedBySelf | null }) {
  if (!removedBySelf) return null;
  const action = removedBySelf.kind === "banned" ? "banned from" : "removed from";
  const who = removedBySelf.byDisplayName
    ? ` by ${removedBySelf.byDisplayName}`
    : removedBySelf.byUserId
      ? ` by ${removedBySelf.byUserId}`
      : "";
  const why = removedBySelf.reason ? `: ${removedBySelf.reason}` : "";
  return (
    <div
      role="status"
      data-testid="timeline-removed"
      style={{
        height: 48,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        padding: "0 var(--chat-space-4)",
        margin: "var(--chat-composer-padding)",
        background: "transparent",
        color: "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-md-size)",
        lineHeight: "var(--chat-type-body-md-line)",
      }}
    >
      You were {action} this room{who}
      {why}. You can’t send messages here.
    </div>
  );
}

function ArchiveGlyph() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 8v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8" />
      <path d="M1 3h22v5H1z" />
      <path d="m8 13 4 4 4-4" />
      <path d="M12 17V9" />
    </svg>
  );
}

function ChevronDownGlyph() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// Test-only seam. Production code does not import these symbols.
// Mirrors the `__matrixRustTestUtils` convention from `matrix-sdk-rust.ts`.
export const __timelineTestUtils = {
  AttachmentUnavailable,
  buildTimelineRows,
  buildRowIndexByEventId,
  StateEventRunRow,
  LockedRunRow,
  isVisibleTimelineEvent,
  isUndecryptableTimelineEvent,
  // Reading-anchor + unread-boundary decision logic (pure; the DOM
  // measurement wrapper around these is exercised by the Playwright
  // spec). Exported so the unit suite can assert the anchoring contract
  // directly.
  resolveReadingAnchor,
  supersedesReadingAnchorOnInput,
  retainEntryReadMarker,
  resolveUnreadBoundary,
  measureEventOffset,
  findEventRowNode,
  firstViewportIntersectingRow,
};
